import { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { marked } from "marked";
import Header from "components/common/Header";
import Sidebar from "components/common/Sidebar";
import useAppState from "context/useAppState";
import { MARKDOWN_VIEWER_CUSTOM_STYLING } from "utils/constant";
import { formatDate } from "utils/function";
import { toast } from "react-toastify";
import { checkProjectStatus, copyImageForAnnotation, getMarkdownContent, getPatentVijilizedDetails, getPatentVijilizedDetailsSlim, reanalyzeSingleClaimElement, updateReanalysisResult, updateVijilizationStatus, updateCitationsForSingleComparison, changePatentImage, rotateVijilizeImage } from "services/project";
import ReAnalyzeModal from "components/common/modals/ReAnalyzeModal";
import ChangeVijilizeImageModal from "components/common/modals/ChangeVijilizeImageModal";
import CitationsSidebar from "components/CitationsSidebar";
import ExpandableZoomableImage from "components/ExpandableZoomableImage";
import ZoomableImage from "components/ZoomableImage";
import CloseIcon from "@mui/icons-material/Close";
import PerFeatureAnalysisView, {
  FeatureColumnData,
  isPdfUrl,
  pageFromImageUrl,
  withPage,
} from "components/PerFeatureAnalysisView";
import loginLogo from "assets/images/login_logo.svg";
import {
  pfEmphFig,
  pfBuildEmphParts,
  pfStripAccused,
  pfExtractAccusedUrl,
  pfExtractClause,
  pfNormClause,
  pfExtractFigureInsight,
  pfEscapeHtml,
  pfNorm,
  pfHex,
  pfNormFig,
  pfParseChart,
  pfStripNums,
  pfOwnChartRows,
  flagNoNumeralMd,
  ensureNoRefStyles,
  pfCellHtml,
  pfFeatureCellHtml,
  pfNoMatchCellHtml,
  pfIsAnnotatedImgUrl,
  pfBuildOwnChartHtml,
  pfClaimHexEntries,
  pfChipCol,
  pfClaimHexForSub,
  pfExtendChipPrefix,
  pfClaimPartEntries,
  pfMergeParts,
} from "utils/pfMarkdown";


declare global {
  interface Window {
    handleImageEditClick: (s3Path: string) => void;
    handleReAnalyzeClick: (srcType?: "target" | "source") => void;
    handleInlineImageRotate?: (btn: HTMLElement) => void;
  }
}

interface VijilizeClaimData {
  activeIndex: {
    patentId: string;
    claimIndex: number;
  };
  selectedClaim: any;
  selectedSimilarity: any;
  clickedItemIndex: number;
  isMatchedItem: boolean;
  annotationId?: string;
  storedClaimKey?: string;
  comparisonId?: string;
  sourcePatentId?: string;
  targetDocumentId?: string;
}

// Per-feature view helpers now live in utils/pfMarkdown (shared with the
// claim view's inline ClauseFeatureStrip).

const MarkdownViewer = () => {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [imagesLoaded, setImagesLoaded] = useState<boolean>(false);
  const [imagesCount, setImagesCount] = useState<number>(0);
  const [loadedImagesCount, setLoadedImagesCount] = useState<number>(0);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [isAnnotationLoading, setIsAnnotationLoading] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchedMarkdownContentData, setFetchedMarkdownContentData] =
    useState<any>({});
  const [showReAnalyzeModal, setShowReAnalyzeModal] = useState<boolean>(false);
  const [isVijilizing, setIsVijilizing] = useState<boolean>(false);
  const [actualComparisonId, setActualComparisonId] = useState<string>("");
  const [citationsData, setCitationsData] = useState<any[]>([]);
  const [selectedCitation, setSelectedCitation] = useState<any>(null);
  const [isShowSideBarCitations, setIsShowSideBarCitations] = useState<boolean>(false);
  const [citationsTab, setCitationsTab] = useState<"citations" | "parts">("citations");

  // Parts list for the current patent, read from the same stored claim structure the legends use.
  const partsListData = useMemo(() => {
    try {
      const cd = JSON.parse(sessionStorage.getItem("vijilizeClaimData") || "null");
      const sc = cd?.selectedClaim;
      if (!sc) return [];
      const parts = pfMergeParts(pfClaimPartEntries(sc));
      if (parts.length === 0) return [];
      return [{ patentId: cd?.annotationData?.patentId || "Patent", parts }];
    } catch {
      return [];
    }
  }, []);
  const [reanalyzeSrcType, setReanalyzeSrcType] = useState<"target" | "source">("target");
  const [showPatentImageModal, setShowPatentImageModal] = useState<boolean>(false);
  // Change-Image in flight: persist call + new figure preload
  const [patentImageSaving, setPatentImageSaving] = useState<boolean>(false);
  const [figureInsightHtml, setFigureInsightHtml] = useState<string>("");
  // Bumped after the classic chart's Analysis cells are swapped for Figure
  // Insights so the citation walker re-runs over the newly inserted tokens.
  const [insightSwapKey, setInsightSwapKey] = useState<number>(0);
  const [accusedImageUrl, setAccusedImageUrl] = useState<string>("");
  // Per-feature column view: undefined = still assembling, null = fall back to
  // the classic single-item layout, array = render the column view.
  const [vijilizeDetails, setVijilizeDetails] = useState<any>(null);
  const [featureColumns, setFeatureColumns] = useState<
    FeatureColumnData[] | null | undefined
  >(undefined);
  const [featureColumnsMeta, setFeatureColumnsMeta] = useState<{
    clauseHtml: string;
    claimLabel: string;
    commonChartHtml: string;
    commonChartLabel?: string;
    currentAnalysisHtml: string;
    currentSubFeature: string;
  }>({
    clauseHtml: "",
    claimLabel: "",
    commonChartHtml: "",
    currentAnalysisHtml: "",
    currentSubFeature: "",
  });
  const [pendingAction, setPendingAction] = useState<
    null | "reanalyze" | "changeImage"
  >(null);
  const [expandedColumnKey, setExpandedColumnKey] = useState<string | null>(
    null
  );
  // Toggle between the new per-feature column view ("graph") and the classic
  // single-item claim-chart layout ("classic"). Only shown when the column
  // view is available; classic remains the fallback either way. The claim
  // view's chart-expand button asks for "classic" via the stored claim data.
  const [detailsViewMode, setDetailsViewMode] = useState<"graph" | "classic">(
    () => {
      try {
        return JSON.parse(sessionStorage.getItem("vijilizeClaimData") || "{}")
          ?.preferredDetailsView === "classic"
          ? "classic"
          : "graph";
      } catch {
        return "graph";
      }
    }
  );
  // Classic-view claim chart: injected cells beside "Patent Element" /
  // "Accused Instrumentality" that hold the zoomable figure/accused images.
  // Each cell keeps its row's Feature text so the image can be resolved from
  // that feature's own column data (siblings have their own images).
  const [chartImageCells, setChartImageCells] = useState<{
    patent: { el: HTMLElement; feature: string }[];
    accused: { el: HTMLElement; feature: string }[];
  }>({ patent: [], accused: [] });
  // Same injected image cells for the Feature View's common claim chart.
  // colKey: design rows resolve by column key, not by feature-cell text.
  const [featureChartImageCells, setFeatureChartImageCells] = useState<{
    patent: { el: HTMLElement; feature: string; colKey?: string }[];
    accused: { el: HTMLElement; feature: string; colKey?: string }[];
  }>({ patent: [], accused: [] });
  // bucket/annotation/image context per column key (not needed for rendering)
  const colMetaRef = useRef<Record<string, any>>({});
  // getMarkdownContent responses per annotation_id so pill switches don't refetch
  const siblingCacheRef = useRef<Record<string, any>>({});

  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { id, vijilizeId } = useParams();
  const {
    setAnnotationS3Path,
    originalImagePath,
    markdownContent,
    markdownS3Path,
    setImageCoordinates,
    setMarkdownS3Path,
    setMarkdownContent,
    setOriginalImagePath,
    patentImage,
    setPatentImage,
    setFontInfo,
    setAnnotationData,
    setWalkthroughState,
  } = useAppState("vijilize");

  useEffect(() => {
    fetchProjectStatus()
  }, [])

  // Publish this page so the Header surfaces the "Working with the Analysis"
  // guided walkthrough. Reset on unmount.
  useEffect(() => {
    setWalkthroughState({
      page: "view-more-detail",
      hasSourcePatent: false,
      hasTarget: false,
      addPatentModalOpen: false,
    });
    return () => {
      setWalkthroughState({
        page: "",
        hasSourcePatent: false,
        hasTarget: false,
        addPatentModalOpen: false,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (vijilizeId && /^[0-9a-fA-F]{24}$/.test(vijilizeId)) {
      (async () => {
        try {
          // Slim payload (pre-warmed by the claim page's shared cache);
          // monolith fallback for old backends
          let patentData: any;
          try {
            patentData = await getPatentVijilizedDetailsSlim(vijilizeId);
          } catch {
            patentData = await getPatentVijilizedDetails(vijilizeId);
          }
          if (patentData?.citations) {
            setCitationsData(patentData.citations);
          }
          setVijilizeDetails(patentData || null);
        } catch (err) {
          console.error("Error fetching citations data:", err);
          // Without the full details we can't assemble sibling columns.
          setFeatureColumns(null);
        }
      })();
    } else {
      setFeatureColumns(null);
    }
  }, [vijilizeId]);

  const fetchProjectStatus = async () => {
    try {
      const { data } = await checkProjectStatus(id)
      console.log({ data })
    } catch (error: any) {
      if (error?.statusCode == 404) {
        navigate('/project')
      }
      console.log({ getProjectStatusError: error })
    }
  }

  useEffect(() => {
    setRefreshKey((prev) => prev + 1);
    setIsLoading(true);
    setError(null);
  }, [location.pathname]);

  // Clean up global function when component unmounts
  useEffect(() => {
    return () => {
      // @ts-ignore
      window.handleImageEditClick = undefined;
    };
  }, []);

  // Handle forceRefresh flag from navigation state
  useEffect(() => {
    if (location.state?.forceRefresh) {
      console.log("Force refresh triggered from navigation state");
      setRefreshKey((prev) => prev + 1);
      // Clear the state to prevent re-triggering on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (!vijilizeId) {
      setIsLoading(false);
      setError("Missing required data to fetch content");
      return;
    }

    const fetchMarkdownContent = async (params: {
      comparisonId: string;
      patentId: string;
      claimKey: string;
      isMatchedItem: boolean;
      annotationId: string;
    }) => {
      // Store the actual comparison ID for later use
      setActualComparisonId(vijilizeId || "");

      if (!params.annotationId) {
        if (markdownContent) {
          setFetchedMarkdownContentData({
            markdown_content: markdownContent,
          });
          setIsLoading(false);
          return;
        }
      }

      try {
        const response: any = await getMarkdownContent(
          params.comparisonId,
          params.patentId,
          params.claimKey,
          params.isMatchedItem,
          params.annotationId
        );

        if (response?.success && response?.data) {
          setFetchedMarkdownContentData(response?.data);
          response?.data?.markdownS3Path &&
            setAnnotationS3Path(response?.data?.markdownS3Path);
          response?.data?.markdownS3Path &&
            setMarkdownS3Path(response?.data?.markdownS3Path);
          response?.data?.originalImagePath &&
            setOriginalImagePath(response?.data?.originalImagePath);
          response?.data?.markdownContent &&
            setMarkdownContent(response?.data?.markdownContent);
          response?.data?.imageCoordinates &&
            setImageCoordinates(response?.data?.imageCoordinates);
          response?.data?.patentImage &&
            setPatentImage(response?.data?.patentImage);
          response?.data?.fontInfo &&
            setFontInfo(response?.data?.fontInfo);

          // Store the comparison ID if available in response, otherwise use the one from params
          if (response?.data?.comparisonId) {
            setActualComparisonId(response.data.comparisonId);
          } else if (response?.data?.comparison_id) {
            setActualComparisonId(response.data.comparison_id);
          }
        } else if (!markdownContent) {
          setError(response?.data?.message || "No markdown content found");
        }
      } catch (err: any) {
        console.error("Error fetching markdown content:", err);
        // Keep already-rendered embedded content instead of blanking the page.
        if (!markdownContent) {
          setError(
            err.response?.data?.message || "Failed to fetch markdown content"
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    const run = async () => {
      const claimDataStr = sessionStorage.getItem("vijilizeClaimData");

      if (claimDataStr) {
        let claimData: VijilizeClaimData;
        try {
          claimData = JSON.parse(claimDataStr);
        } catch (e) {
          console.error(
            "Error parsing vijilizeClaimData from sessionStorage",
            e
          );
          setIsLoading(false);
          setError("Invalid claim data format");
          return;
        }
        const { activeIndex, isMatchedItem, annotationId, storedClaimKey } =
          claimData;
        if (!activeIndex?.patentId) {
          setIsLoading(false);
          setError("Missing patentId in claim data");
          return;
        }
        await fetchMarkdownContent({
          comparisonId: vijilizeId,
          patentId: activeIndex.patentId,
          claimKey: storedClaimKey || `claim${activeIndex.claimIndex + 1}`,
          isMatchedItem: isMatchedItem,
          annotationId: annotationId || "",
        });
        return;
      }

      // No in-app context (e.g. a shared / guided-walkthrough deep link): find the
      // first analysis element that already carries markdown content in the
      // vijilization results and render it directly. The markdown-content
      // endpoint needs a real per-element annotation_id (which the in-app "View
      // More Details" click supplies) — a deep link has none, so we render the
      // embedded content instead of calling it. This keeps the analysis page (and
      // its Annotation button) available for the guided walkthrough.
      try {
        const details: any = await getPatentVijilizedDetails(vijilizeId);
        const results: any[] = Array.isArray(
          details?.summarized_similarity_results
        )
          ? details.summarized_similarity_results
          : [];
        let found: any = null;
        let foundCtx: any = null;
        for (const entry of results) {
          for (const pid of Object.keys(entry || {})) {
            const claims = entry[pid] || {};
            for (const ck of Object.keys(claims)) {
              const simItems = Array.isArray(claims[ck]) ? claims[ck] : [];
              for (const sim of simItems) {
                const matched = (sim?.matched_items || []).find(
                  (c: any) => c?.markdown_content
                );
                found =
                  matched ||
                  (sim?.non_matched_items || []).find(
                    (c: any) => c?.markdown_content
                  );
                if (found) {
                  foundCtx = {
                    pid,
                    ck,
                    target: sim?.targetDocumentId,
                    isMatched: !!matched,
                  };
                  break;
                }
              }
              if (found) break;
            }
            if (found) break;
          }
          if (found) break;
        }
        if (!found?.markdown_content) {
          setIsLoading(false);
          setError("Missing required data to fetch content");
          return;
        }
        found.image_coordinates && setImageCoordinates(found.image_coordinates);
        found.original_image_path &&
          setOriginalImagePath(found.original_image_path);
        found.markdown_image?.[0] && setMarkdownS3Path(found.markdown_image[0]);
        found.markdown_image?.[0] &&
          setAnnotationS3Path(found.markdown_image[0]);
        setMarkdownContent(found.markdown_content);
        // Store the found context so the page behaves like an in-app open.
        if (foundCtx) {
          const claimIdx = parseInt(
            String(foundCtx.ck).replace(/\D/g, ""),
            10
          );
          sessionStorage.setItem(
            "vijilizeClaimData",
            JSON.stringify({
              activeIndex: {
                patentId: foundCtx.pid,
                claimIndex: Number.isNaN(claimIdx) ? 0 : claimIdx - 1,
              },
              storedClaimKey: foundCtx.ck,
              targetDocumentId: foundCtx.target,
              isMatchedItem: foundCtx.isMatched,
              annotationId: found.annotation_id || undefined,
            })
          );
          if (found.annotation_id) {
            setFetchedMarkdownContentData({
              markdown_content: found.markdown_content,
            });
            setActualComparisonId(vijilizeId || "");
            setRefreshKey((p) => p + 1);
            return;
          }
        }
        setFetchedMarkdownContentData({
          markdown_content: found.markdown_content,
        });
        setActualComparisonId(vijilizeId || "");
        setIsLoading(false);
      } catch (e) {
        console.error("Error building default analysis context:", e);
        setIsLoading(false);
        setError("Missing required data to fetch content");
      }
    };

    run();
  }, [vijilizeId, refreshKey]);

  useEffect(() => {
    const contentToRender =
      fetchedMarkdownContentData?.markdown_content || markdownContent;

    if (!contentToRender) {
      if (!isLoading && !error) {
        navigate(-1);
      }
      return;
    }

    try {
      const customStyles = MARKDOWN_VIEWER_CUSTOM_STYLING;

      marked.setOptions({});

      window.handleImageEditClick = async (imagePathOverride?: any) => {
        if (id) {
          try {
            // Clear any sibling context so the editor saves to the current item
            setAnnotationData(null);
            setIsAnnotationLoading(true);
            const buttons = document.querySelectorAll(".edit-image-button");
            buttons.forEach((button) => {
              button.classList.add("loading");
            });

            let selectedTeam: any = JSON.parse(
              localStorage.getItem("selectedTeam") as string
            );
            let team_id: string = selectedTeam.currentTeamId;

            const response = await copyImageForAnnotation(
              (typeof imagePathOverride === "string" && imagePathOverride) ||
              fetchedMarkdownContentData?.original_image_path ||
              originalImagePath,
              id,
              team_id
            );

            if (response?.data && response?.data?.annotation_s3_path) {
              const annotationS3Path = response?.data?.annotation_s3_path;

              setAnnotationS3Path(annotationS3Path);
              navigate(`/project/${id}/vijilize/${vijilizeId}/annotation`);
            } else {
              setIsAnnotationLoading(false);
              buttons.forEach((button) => {
                button.classList.remove("loading");
              });
              toast.error("Failed to prepare image for annotation");
            }
          } catch (error) {
            console.error("Error preparing image for annotation:", error);

            setIsAnnotationLoading(false);
            const buttons = document.querySelectorAll(".edit-image-button");
            buttons.forEach((button) => {
              button.classList.remove("loading");
            });

            toast.error("Error preparing image for annotation");
          }
        }
      };

      window.handleReAnalyzeClick = (srcType?: "target" | "source") => {
        setReanalyzeSrcType(srcType || "target");
        setShowReAnalyzeModal(true);
      };

      const spinnerStyles = `
        <style>
          .edit-image-button .loading-spinner {
            display: none;
          }
          .edit-image-button.loading .loading-spinner {
            display: inline-block;
            width: 12px;
            height: 12px;
            border: 2px solid rgba(255,255,255,0.3);
            border-radius: 50%;
            border-top-color: white;
            animation: spin 1s ease-in-out infinite;
            margin-left: 4px;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        </style>
      `;

      if (!document.getElementById("spinner-styles")) {
        const styleEl = document.createElement("style");
        styleEl.id = "spinner-styles";
        styleEl.innerHTML = spinnerStyles
          .replace("<style>", "")
          .replace("</style>", "");
        document.head.appendChild(styleEl);
      }

      marked.use({
        renderer: {
          image(token) {
            const { href, title, text } = token;
            return `<div class="markdown-image-container" style="position: relative; display: inline-block; max-width: 100%;">
              <img src="${href}" alt="${text || ""}" title="${title || ""
              }" class="markdown-image" data-loading="true" style="max-width: 100%; height: auto; width: auto; display: block;" />
              <div style="position: absolute; top: 8px; right: 8px; display: flex; gap: 4px; z-index: 10;">
                <button type="button" onclick="window.handleImageEditClick()" class="edit-image-button" style="background-color: #1751D0; color: white; border: none; border-radius: 4px; padding: 4px 8px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                  Annotation
                  <span class="loading-spinner"></span>
                </button>
                <button type="button" onclick="window.handleReAnalyzeClick()" class="reanalyze-button" style="background-color: #1751D0; color: white; border: none; border-radius: 4px; padding: 4px 8px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                  </svg>
                  ReAnalyze
                </button>
                <button type="button" onclick="window.handleInlineImageRotate?.(this)" title="Rotate 90° clockwise (saved)" class="rotate-image-button" style="background-color: #1751D0; color: white; border: none; border-radius: 4px; padding: 4px 8px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.55 5.55L11 1v3.07C7.06 4.56 4 7.92 4 12s3.05 7.44 7 7.93v-2.02c-2.84-.48-5-2.94-5-5.91s2.16-5.43 5-5.91V10l4.55-4.45z"></path>
                    <path d="M19.93 11c-.17-1.39-.72-2.73-1.62-3.89l-1.42 1.42c.54.75.88 1.6 1.02 2.47h2.02zM13 17.91v2.02c1.39-.17 2.74-.71 3.9-1.61l-1.44-1.44c-.75.54-1.59.89-2.46 1.03zm3.89-2.42l1.42 1.41c.9-1.16 1.45-2.5 1.62-3.9h-2.02c-.14.87-.48 1.72-1.02 2.49z"></path>
                  </svg>
                  Rotate
                </button>
              </div>
            </div>`;
          },
        },
      });

      // The accused-instrumentality image is rendered by <ComparisonImageCard>, but
      // we load it from the SAME URL the markdown used before — the presigned URL in
      // the "#### Accused Instrumentality:" section — not original_image_path (which
      // is a raw s3:// path that won't load in <img>).
      const accusedMatch = contentToRender.match(
        /####\s*Accused Instrumentality:[\s\S]*?!\[[^\]]*\]\(\s*([^)\s]+)/i
      );
      setAccusedImageUrl(accusedMatch ? accusedMatch[1] : "");

      // Remove the accused section so its image isn't shown twice.
      const withoutAccused = contentToRender
        .replace(/\n*####\s*Accused Instrumentality:[\s\S]*$/i, "")
        .trimEnd();

      // Split the remaining markdown so the images can sit between the claim chart
      // and the figure insight: chart -> images -> figure insight.
      const fiIndex = withoutAccused.search(/####\s*Figure Insight:/i);
      const chartMd =
        fiIndex >= 0 ? withoutAccused.slice(0, fiIndex).trimEnd() : withoutAccused;
      const figureInsightMd = fiIndex >= 0 ? withoutAccused.slice(fiIndex) : "";

      // Figure-ref and claim-part emphasis — the SHARED helpers (numeral-list
      // aware), seeded from the "#### Feature:" clause so only its numerals
      // get boxed. Was a stale local duplicate with a single-numeral regex.
      const featureMatch = chartMd.match(/####\s*Feature:\s*([\s\S]*?)(?:\n\s*\||$)/i);
      const emphFig = pfEmphFig;
      const emphParts = pfBuildEmphParts(featureMatch ? featureMatch[1] : "");

      const figRefStyle =
        "<style>.markdown-content .fig-ref{font-weight:700;background:#f3f4f6;border:1px solid #e5e7eb;border-radius:4px;padding:0 4px;}</style>";

      setHtmlContent(
        customStyles +
          figRefStyle +
          (marked.parse(flagNoNumeralMd(emphFig(emphParts(chartMd)))) as string)
      );
      setFigureInsightHtml(
        figureInsightMd
          ? (marked.parse(emphFig(emphParts(figureInsightMd))) as string)
          : ""
      );

      setImagesLoaded(false);
      setLoadedImagesCount(0);
      setImagesCount(0);
    } catch (error) {
      console.error("Error parsing markdown:", error);
      setHtmlContent("<p>Error parsing content</p>");
      setImagesLoaded(true);
    }
  }, [fetchedMarkdownContentData, navigate, refreshKey, isLoading, error]);

  useEffect(() => {
    if (!htmlContent || !contentRef.current || citationsData.length === 0) return;

    const container = contentRef.current;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const regex = /\[\[(\d+(?:\s*,\s*\d+)*)\]\]|\[(\d+(?:\s*,\s*\d+)*)\]/g;
    const nodesToProcess: { node: Text; matches: { index: number; length: number; numbers: string[] }[] }[] = [];

    while (walker.nextNode()) {
      const textNode = walker.currentNode as Text;
      const text = textNode.textContent || "";
      const matches: { index: number; length: number; numbers: string[] }[] = [];
      let match;
      regex.lastIndex = 0;
      while ((match = regex.exec(text)) !== null) {
        const group = (match[1] || match[2])
          .split(",")
          .map((n: string) => n.trim())
          .sort((a: string, b: string) => Number(a) - Number(b));
        matches.push({ index: match.index, length: match[0].length, numbers: group });
      }
      if (matches.length > 0) {
        nodesToProcess.push({ node: textNode, matches });
      }
    }

    nodesToProcess.forEach(({ node, matches }) => {
      const parent = node.parentNode;
      if (!parent) return;
      const text = node.textContent || "";
      const fragment = document.createDocumentFragment();
      let lastIdx = 0;

      matches.forEach(({ index, length, numbers }) => {
        if (index > lastIdx) {
          fragment.appendChild(document.createTextNode(text.slice(lastIdx, index)));
        }
        numbers.forEach((num, i) => {
          const sup = document.createElement("sup");
          const link = document.createElement("span");
          link.textContent = num;
          link.style.color = "#1751D0";
          link.style.cursor = "pointer";
          link.className = "citation-link";
          link.dataset.citationNumber = num;
          sup.appendChild(link);
          fragment.appendChild(sup);
          if (i < numbers.length - 1) {
            const comma = document.createElement("sup");
            comma.textContent = ",";
            fragment.appendChild(comma);
          }
        });
        lastIdx = index + length;
      });

      if (lastIdx < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIdx)));
      }
      parent.replaceChild(fragment, node);
    });

    const handleCitationLinkClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("citation-link") && target.dataset.citationNumber) {
        const num = target.dataset.citationNumber;
        setSelectedCitation({ value: num, label: `${num}` });
        setIsShowSideBarCitations(true);
      }
    };

    container.addEventListener("click", handleCitationLinkClick);
    return () => {
      container.removeEventListener("click", handleCitationLinkClick);
    };
  }, [htmlContent, figureInsightHtml, citationsData, featureColumns, expandedColumnKey, detailsViewMode, insightSwapKey]);

  // Classic view: add a "Patent Figure" column beside "Patent Element" and an
  // "Accused Image" column beside "Accused Instrumentality" in the claim
  // chart. The chart is marked-rendered HTML, so the cells are injected into
  // the table here and the images rendered into them via portals (see the
  // createPortal calls in the JSX) with the same ZoomableImage pan/zoom as
  // the big comparison card below the chart.
  useEffect(() => {
    const classicVisible =
      !isLoading &&
      !error &&
      !(
        featureColumns &&
        featureColumns.length > 0 &&
        detailsViewMode === "graph"
      );
    const container = contentRef.current;
    const clearCells = () =>
      setChartImageCells((prev) =>
        prev.patent.length || prev.accused.length
          ? { patent: [], accused: [] }
          : prev
      );
    if (!classicVisible || !container) {
      clearCells();
      return;
    }
    const table: HTMLElement | null = container.querySelector(
      ".markdown-content table"
    );
    if (!table) {
      clearCells();
      return;
    }
    if (table.dataset.imageCols === "1") return;

    const rows = Array.from(table.querySelectorAll("tr"));
    if (!rows.length) return;
    const headers = Array.from(rows[0].children);
    const findIdx = (re: RegExp) =>
      headers.findIndex((th) => re.test((th.textContent || "").trim()));
    const featureIdx = Math.max(findIdx(/^feature$/i), 0);
    const patentIdx = findIdx(/patent\s*element/i);
    const accusedIdx = findIdx(/accused\s*(?:instrumentality|implementation)/i);

    const anyPatentImg =
      patentImage?.patent_figure_path ||
      (featureColumns || []).some((c) => c.patentFigureUrl);
    const anyAccusedImg =
      accusedImageUrl || (featureColumns || []).some((c) => c.accusedImageUrl);

    const inserts: { idx: number; kind: "patent" | "accused"; label: string }[] =
      [];
    if (accusedIdx >= 0 && anyAccusedImg) {
      inserts.push({ idx: accusedIdx, kind: "accused", label: "Accused Image" });
    }
    if (patentIdx >= 0 && anyPatentImg) {
      inserts.push({ idx: patentIdx, kind: "patent", label: "Patent Figure" });
    }
    if (!inserts.length) return;
    // Right-to-left so the earlier column's index stays valid after inserting
    inserts.sort((a, b) => b.idx - a.idx);

    const cells: {
      patent: { el: HTMLElement; feature: string }[];
      accused: { el: HTMLElement; feature: string }[];
    } = { patent: [], accused: [] };
    rows.forEach((row, rowIndex) => {
      const feature =
        rowIndex === 0
          ? ""
          : (row.children[featureIdx]?.textContent || "").trim();
      inserts.forEach(({ idx, kind, label }) => {
        const ref = row.children[idx];
        if (!ref) return;
        const cell = document.createElement(rowIndex === 0 ? "th" : "td");
        cell.dataset.pfImgCell = "1";
        cell.style.minWidth = "14rem";
        cell.style.verticalAlign = "top";
        if (rowIndex === 0) {
          cell.textContent = label;
        } else {
          cells[kind].push({ el: cell, feature });
        }
        ref.after(cell);
      });
    });
    table.dataset.imageCols = "1";
    setChartImageCells(cells);
  }, [
    htmlContent,
    isLoading,
    error,
    imagesLoaded,
    imagesCount,
    featureColumns,
    detailsViewMode,
    patentImage,
    accusedImageUrl,
    refreshKey,
  ]);

  // Classic view: the Analysis column duplicates each feature's Figure Insight
  // minus its citations, so swap every row's Analysis cell for that feature's
  // Figure Insight (its [n] tokens are converted to clickable citations by the
  // walker effect, re-triggered via insightSwapKey). The standalone Figure
  // Insight block below the images is hidden when this swap is available.
  useEffect(() => {
    const classicVisible =
      !isLoading &&
      !error &&
      !(
        featureColumns &&
        featureColumns.length > 0 &&
        detailsViewMode === "graph"
      );
    const container = contentRef.current;
    if (!classicVisible || !container || !featureColumns?.length) return;
    const table: HTMLElement | null = container.querySelector(
      ".markdown-content table"
    );
    if (!table || table.dataset.insightSwap === "1") return;
    const rows = Array.from(table.querySelectorAll("tr"));
    if (!rows.length) return;
    const headers = Array.from(rows[0].children);
    const analysisIdx = headers.findIndex((th) =>
      /analysis/i.test((th.textContent || "").trim())
    );
    if (analysisIdx < 0) return;
    const featureIdx = Math.max(
      headers.findIndex((th) => /^feature$/i.test((th.textContent || "").trim())),
      0
    );
    let swapped = false;
    rows.slice(1).forEach((row) => {
      const feature = (row.children[featureIdx]?.textContent || "").trim();
      const insight = resolveRowFeatureColumn(feature)?.figureInsightHtml;
      const cell = row.children[analysisIdx] as HTMLElement | undefined;
      if (insight && cell) {
        cell.innerHTML = insight;
        swapped = true;
      }
    });
    if (swapped) {
      table.dataset.insightSwap = "1";
      setInsightSwapKey((p) => p + 1);
    }
  }, [
    htmlContent,
    isLoading,
    error,
    imagesLoaded,
    featureColumns,
    detailsViewMode,
    refreshKey,
  ]);

  // Feature View: mirror the classic chart's "Patent Figure" / "Accused
  // Image" columns in the common claim chart — cells injected into the
  // marked-built table, images rendered into them via portals per row.
  useEffect(() => {
    const clearCells = () =>
      setFeatureChartImageCells((prev) =>
        prev.patent.length || prev.accused.length
          ? { patent: [], accused: [] }
          : prev
      );
    const graphVisible =
      !isLoading &&
      !error &&
      featureColumns &&
      featureColumns.length > 0 &&
      detailsViewMode === "graph";
    const container = contentRef.current;
    if (!graphVisible || !container) {
      clearCells();
      return;
    }
    const table: HTMLElement | null = container.querySelector(
      ".feature-common-chart table"
    );
    if (!table) {
      clearCells();
      return;
    }
    if (table.dataset.imageCols === "1") return;

    const rows = Array.from(table.querySelectorAll("tr"));
    if (!rows.length) return;
    const headers = Array.from(rows[0].children);
    const findIdx = (re: RegExp) =>
      headers.findIndex((th) => re.test((th.textContent || "").trim()));
    const featureIdx = Math.max(findIdx(/^feature$/i), 0);
    const patentIdx = findIdx(/patent\s*element/i);
    const accusedIdx = findIdx(/accused\s*(?:instrumentality|implementation)/i);

    const anyPatentImg = (featureColumns || []).some((c) => c.patentFigureUrl);
    const anyAccusedImg = (featureColumns || []).some((c) => c.accusedImageUrl);

    const inserts: { idx: number; kind: "patent" | "accused"; label: string }[] =
      [];
    if (accusedIdx >= 0 && anyAccusedImg) {
      inserts.push({ idx: accusedIdx, kind: "accused", label: "Accused Image" });
    }
    if (patentIdx >= 0 && anyPatentImg) {
      inserts.push({ idx: patentIdx, kind: "patent", label: "Patent Figure" });
    }
    if (!inserts.length) return;
    inserts.sort((a, b) => b.idx - a.idx);

    const cells: {
      patent: { el: HTMLElement; feature: string; colKey?: string }[];
      accused: { el: HTMLElement; feature: string; colKey?: string }[];
    } = { patent: [], accused: [] };
    rows.forEach((row, rowIndex) => {
      // Design rows: data-col-key names the owning column; "" = no images.
      const rowColKey = row.getAttribute("data-col-key");
      const feature =
        rowIndex === 0
          ? ""
          : (row.children[featureIdx]?.textContent || "").trim();
      inserts.forEach(({ idx, kind, label }) => {
        const ref = row.children[idx];
        if (!ref) return;
        const cell = document.createElement(rowIndex === 0 ? "th" : "td");
        cell.dataset.pfImgCell = "1";
        cell.style.minWidth = "14rem";
        cell.style.verticalAlign = "top";
        if (rowIndex === 0) {
          cell.textContent = label;
        } else if (rowColKey !== "") {
          cells[kind].push({ el: cell, feature, colKey: rowColKey || undefined });
        }
        ref.after(cell);
      });
    });
    table.dataset.imageCols = "1";
    setFeatureChartImageCells(cells);
  }, [
    featureColumnsMeta,
    featureColumns,
    detailsViewMode,
    isLoading,
    error,
    refreshKey,
  ]);

  // Source-document links for the expanded image overlays: the patent PDF
  // (presigned file_url from vijilize-details `source`) and the target
  // document (website URL resolved by the backend, or a file target's
  // presigned targetDocumentIdUrl).
  const { patentPdfLink, targetDocLink } = useMemo(() => {
    let ctx: any = null;
    try {
      ctx = JSON.parse(sessionStorage.getItem("vijilizeClaimData") || "null");
    } catch {
      /* noop */
    }
    const sources: any[] = Array.isArray(vijilizeDetails?.source)
      ? vijilizeDetails.source
      : [];
    const patentPdfLink =
      (ctx?.sourcePatentId &&
        sources.find((s) => String(s.patent_id) === String(ctx.sourcePatentId))
          ?.file_url) ||
      sources[0]?.file_url ||
      "";
    const sims: any[] = Array.isArray(ctx?.selectedSimilarity)
      ? ctx.selectedSimilarity
      : [];
    const sim = sims.find(
      (s) => s?.targetDocumentId === ctx?.targetDocumentId
    );
    let targetDocLink = sim?.targetDocumentIdUrl || "";
    if (!targetDocLink && /^https?:\/\//i.test(ctx?.targetDocumentId || "")) {
      targetDocLink = ctx.targetDocumentId;
    }
    return { patentPdfLink, targetDocLink };
  }, [vijilizeDetails, refreshKey]);

  // Snapshot of the current website target, matched against the
  // vijilize-details `compare` array where the backend attaches
  // snapshot_date (S3 LastModified of the PDF the Python pipeline captured at
  // vijilization time) and snapshot_url (7-day presigned link, re-minted on
  // every fetch). Both null for file targets or never-snapshotted URLs.
  const { targetSnapshotDate, targetSnapshotUrl } = useMemo(() => {
    let ctx: any = null;
    try {
      ctx = JSON.parse(sessionStorage.getItem("vijilizeClaimData") || "null");
    } catch {
      /* noop */
    }
    const compare: any[] = Array.isArray(vijilizeDetails?.compare)
      ? vijilizeDetails.compare
      : [];
    const target = ctx?.targetDocumentId || "";
    const hit = compare.find(
      (c) => c?.url && (c.url === target || c.url === targetDocLink)
    );
    return {
      targetSnapshotDate: hit?.snapshot_date || null,
      targetSnapshotUrl: hit?.snapshot_url || null,
    };
  }, [vijilizeDetails, refreshKey, targetDocLink]);

  // Match a chart row's Feature cell to the feature column (sibling item) it
  // came from — normalized substring both ways, longest sub-feature wins so
  // e.g. "grip" can't steal "one hand gripping apparatus" rows.
  const resolveRowFeatureColumn = (feature: string) => {
    if (!feature || !featureColumns?.length) return undefined;
    const pick = (key: (s: string) => string) => {
      const f = key(feature);
      if (!f) return undefined;
      let best: FeatureColumnData | undefined;
      let bestLen = 0;
      featureColumns.forEach((c) => {
        const s = key(c.subFeature || "");
        if (!s) return;
        if (f.includes(s) || s.includes(f)) {
          if (s.length > bestLen) {
            best = c;
            bestLen = s.length;
          }
        }
      });
      return best;
    };
    // Numeral lists can drift between the chart row and the sub_feature label
    // — retry with the parenthetical numerals stripped before giving up.
    return pick(pfNorm) || pick((s) => pfNorm(pfStripNums(s)));
  };

  // No-match items carry un-annotated images — tag them "No match found".
  const accusedNoMatch = (feature?: string, colKey?: string) => {
    const col = colKey
      ? (featureColumns || []).find((c) => c.key === colKey)
      : feature
      ? resolveRowFeatureColumn(feature)
      : undefined;
    if (col) return !col.isMatched;
    // Unresolved feature rows never get stamped — only the opened item's own
    // chart (no feature ref) may fall back to its stored matched flag.
    if (feature || colKey) return false;
    try {
      const cd = JSON.parse(
        sessionStorage.getItem("vijilizeClaimData") || "{}"
      );
      return cd?.isMatchedItem === false;
    } catch {
      return false;
    }
  };

  // After a rotate-and-save the S3 object (same key) holds the rotated image,
  // but the browser may keep serving its cached copy under the old presigned
  // URL — so swap in the fresh URL wherever the old one appears: main patent
  // figure, accused image, and every feature column. All surfaces (cards,
  // chart-column portals, feature view) read from these states.
  const handleImageRotated = (newUrl: string, oldUrl: string) => {
    setPatentImage((prev: any) =>
      prev?.patent_figure_path === oldUrl
        ? { ...prev, patent_figure_path: newUrl }
        : prev
    );
    setAccusedImageUrl((prev) => (prev === oldUrl ? newUrl : prev));
    setFeatureColumns((prev) =>
      prev
        ? prev.map((c) =>
            c.patentFigureUrl === oldUrl || c.accusedImageUrl === oldUrl
              ? {
                  ...c,
                  patentFigureUrl:
                    c.patentFigureUrl === oldUrl ? newUrl : c.patentFigureUrl,
                  accusedImageUrl:
                    c.accusedImageUrl === oldUrl ? newUrl : c.accusedImageUrl,
                }
              : c
          )
        : prev
    );
  };

  // Figure → legend rows, from the claim structure stored by the claim view
  // (the items' own patent_image.figure_legend arrays are empty in practice —
  // the real legends live on the claim, keyed "FIG_3A" style, exactly like
  // the Claim Figures panel reads them).
  const claimLegendMap = useMemo(() => {
    const map: Record<string, { [k: string]: string }[]> = {};
    try {
      const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
      if (!claimDataStr) return map;
      const sc = JSON.parse(claimDataStr)?.selectedClaim;
      const add = (legends: any) => {
        (Array.isArray(legends) ? legends : []).forEach((l: any) => {
          const k = Object.keys(l || {})[0];
          if (k && Array.isArray(l[k]) && !map[pfNormFig(k)]) {
            map[pfNormFig(k)] = l[k];
          }
        });
      };
      add(sc?.legends);
      (sc?.elements || []).forEach((el: any) => {
        add(el?.legends);
        (el?.features || []).forEach((f: any) => add(f?.legends));
      });
    } catch {}
    return map;
  }, []);

  // Item legend if present, else the claim-level legend for that figure.
  const legendFor = (itemLegend: any, figureNumber: any) =>
    Array.isArray(itemLegend) && itemLegend.length > 0
      ? itemLegend
      : claimLegendMap[pfNormFig(figureNumber)];

  // Legend for an injected Patent Figure chart cell — the row's own feature
  // column when it supplied the figure, else the current item's legend (the
  // same fallback chain the image URL uses).
  const patentCellLegend = (feature: string) => {
    const col = resolveRowFeatureColumn(feature);
    if (col?.patentFigureUrl) return col.figureLegend;
    return legendFor(patentImage?.figure_legend, patentImage?.figure_number);
  };

  // Numbered "10. ear" list below the figure — keeps the chart's Patent
  // Figure column narrow; the expanded overlay still shows it side by side.
  const legendListEl = (legend: any) =>
    Array.isArray(legend) && legend.length > 0 ? (
      <ul className="mt-2 pt-2 border-t border-dashed border-gray-200 max-h-40 overflow-y-auto space-y-1 text-left [scrollbar-width:thin]">
        {legend.map((lg: any, li: number) => {
          const k = Object.keys(lg || {})[0];
          if (!k) return null;
          return (
            <li key={li} className="text-[11.5px] leading-snug text-gray-800">
              {k}. {lg[k]}
            </li>
          );
        })}
      </ul>
    ) : null;

  // Rotate handler for the inline markdown-rendered images (the ones with the
  // Annotation / ReAnalyze overlay). Rendered as raw HTML by marked, so the
  // button reaches back in through this window global.
  useEffect(() => {
    window.handleInlineImageRotate = async (btn: HTMLElement) => {
      const img = btn
        .closest(".markdown-image-container")
        ?.querySelector("img.markdown-image") as HTMLImageElement | null;
      if (!img?.src || btn.dataset.rotating === "1") return;
      const oldUrl = img.src;
      btn.dataset.rotating = "1";
      btn.style.opacity = "0.6";
      try {
        const response: any = await rotateVijilizeImage({
          image_url: oldUrl,
          degrees: 90,
        });
        const newUrl = response?.data?.url;
        if (!newUrl) throw new Error("No URL returned");
        img.src = newUrl;
        handleImageRotated(newUrl, oldUrl);
      } catch (error) {
        console.error("Error rotating image:", error);
        toast.error("Failed to rotate image. Please try again.");
      } finally {
        delete btn.dataset.rotating;
        btn.style.opacity = "";
      }
    };
    return () => {
      window.handleInlineImageRotate = undefined;
    };
    // handleImageRotated only uses stable state setters, so binding the
    // mount-time instance is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- per-feature column assembly ----------
  useEffect(() => {
    if (!vijilizeId) {
      setFeatureColumns(null);
      return;
    }
    if (!vijilizeDetails) return; // details still loading
    let cancelled = false;
    (async () => {
      try {
        const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
        if (!claimDataStr) {
          if (!cancelled) setFeatureColumns(null);
          return;
        }
        const claimData: VijilizeClaimData = JSON.parse(claimDataStr);
        const patentId = claimData?.activeIndex?.patentId;
        const claimKey =
          claimData?.storedClaimKey ||
          `claim${(claimData?.activeIndex?.claimIndex ?? 0) + 1}`;
        if (!patentId) {
          if (!cancelled) setFeatureColumns(null);
          return;
        }

        const results: any[] = Array.isArray(
          vijilizeDetails?.summarized_similarity_results
        )
          ? vijilizeDetails.summarized_similarity_results
          : [];
        let sims: any[] = [];
        for (const entry of results) {
          const claims = entry?.[patentId];
          if (claims && Array.isArray(claims[claimKey])) {
            sims = claims[claimKey];
            break;
          }
        }
        const sim =
          sims.find(
            (s: any) =>
              s?.targetDocumentId &&
              s.targetDocumentId === claimData?.targetDocumentId
          ) || sims[0];
        if (!sim) {
          if (!cancelled) setFeatureColumns(null);
          return;
        }

        const tagged = [
          ...(Array.isArray(sim.matched_items) ? sim.matched_items : []).map(
            (item: any, idx: number) => ({ item, isMatched: true, idx })
          ),
          ...(Array.isArray(sim.non_matched_items)
            ? sim.non_matched_items
            : []
          ).map((item: any, idx: number) => ({ item, isMatched: false, idx })),
        ].filter((e) => e.item && typeof e.item === "object");

        const current =
          (claimData.annotationId &&
            tagged.find(
              (e) => e.item.annotation_id === claimData.annotationId
            )) ||
          tagged.find(
            (e) =>
              e.isMatched === claimData.isMatchedItem &&
              e.idx === claimData.clickedItemIndex
          );
        const hex = current?.item?.element_hex_code;
        const currentClauseNorm = current
          ? pfNormClause(pfExtractClause(current.item.markdown_content || ""))
          : "";

        // Slim payloads carry no markdown_content — bucket sub-features into
        // claim elements via the claim structure (exact text, then name, then
        // numeral overlap), like the claim view's clause grouping.
        const clauseEntriesByElement: {
          norm: string;
          name: string;
          nums: string[];
        }[][] = (() => {
          const sc = claimData?.selectedClaim;
          const out: any[][] = [];
          const push = (arr: any[], p: any) => {
            const desc = String(p?.description || "");
            if (!desc.trim()) return;
            arr.push({
              norm: pfNorm(desc),
              name: pfNorm(desc.replace(/\([^)]*\)/g, " ")),
              nums: (desc.match(/\(([^)]*)\)/g) || [])
                .flatMap((g: string) => g.match(/\d+[a-zA-Z]?/g) || [])
                .map((n: string) => n.toLowerCase()),
            });
          };
          const pre = sc?.claim_preamble || sc?.preamble;
          const preArr: any[] = [];
          if (Array.isArray(pre)) pre.forEach((p: any) => push(preArr, p));
          else if (pre) push(preArr, { description: pre });
          out.push(preArr);
          (sc?.elements || []).forEach((el: any) => {
            const arr: any[] = [];
            (el?.features?.length ? el.features : [el]).forEach((f: any) =>
              push(arr, f)
            );
            out.push(arr);
          });
          return out.some((a) => a.length) ? out : [];
        })();
        const clauseIdxForSub = (sub: any): number => {
          const s = String(sub || "");
          if (!s.trim() || clauseEntriesByElement.length === 0) return -1;
          const norm = pfNorm(s);
          for (let i = 0; i < clauseEntriesByElement.length; i++)
            if (clauseEntriesByElement[i].some((e) => e.norm === norm))
              return i;
          const name = pfNorm(s.replace(/\([^)]*\)/g, " "));
          if (name)
            for (let i = 0; i < clauseEntriesByElement.length; i++)
              if (clauseEntriesByElement[i].some((e) => e.name === name))
                return i;
          const nums = new Set(
            (s.match(/\(([^)]*)\)/g) || [])
              .flatMap((g: string) => g.match(/\d+[a-zA-Z]?/g) || [])
              .map((n: string) => n.toLowerCase())
          );
          if (nums.size) {
            let best = -1;
            let bestHits = 0;
            clauseEntriesByElement.forEach((entries, i) => {
              const hits = entries.reduce(
                (a, e) => a + e.nums.filter((n) => nums.has(n)).length,
                0
              );
              if (hits > bestHits) {
                best = i;
                bestHits = hits;
              }
            });
            if (best >= 0) return best;
          }
          return -1;
        };
        const currentClauseIdx = current
          ? clauseIdxForSub(current.item.sub_feature)
          : -1;

        const isDesign = !!current?.item?.is_design_patent;
        if (
          !current ||
          (!isDesign && !currentClauseNorm && !hex && currentClauseIdx < 0)
        ) {
          if (!cancelled) setFeatureColumns(null);
          return;
        }

        // Siblings = items of the SAME element clause: claim-structure bucket
        // first (works on slim items), then markdown clause text, then hex
        // (unsafe alone — the color palette repeats across elements).
        // Design runs: every figure item of the claim is a sibling column.
        const siblings = tagged.filter((e) => {
          if (isDesign) return !!e.item.is_design_patent;
          if (!e.item.sub_feature || e.item.is_design_patent) return false;
          if (currentClauseIdx >= 0) {
            const idx = clauseIdxForSub(e.item.sub_feature);
            if (idx >= 0) return idx === currentClauseIdx;
          }
          if (currentClauseNorm) {
            const c = pfNormClause(
              pfExtractClause(e.item.markdown_content || "")
            );
            return !!c && c === currentClauseNorm;
          }
          return !!hex && e.item.element_hex_code === hex;
        });
        if (siblings.length === 0) {
          if (!cancelled) setFeatureColumns(null);
          return;
        }

        // Each sibling column pulls that sibling's OWN stored analysis: the
        // markdown-content endpoint presigns its patent figure + markdown URLs.
        const enriched = await Promise.all(
          siblings.map(async (e) => {
            let md: string = e.item.markdown_content || "";
            let figureUrl = "";
            let figureNumber = e.item.patent_image?.figure_number || "";
            let figurePageNumber =
              e.item.patent_image?.figure_page_number ?? null;
            let figureLegend = e.item.patent_image?.figure_legend || [];
            let itemOriginalImagePath = e.item.original_image_path || "";
            if (e.item.annotation_id) {
              try {
                let data = siblingCacheRef.current[e.item.annotation_id];
                if (!data) {
                  const res: any = await getMarkdownContent(
                    vijilizeId,
                    patentId,
                    claimKey,
                    e.isMatched,
                    e.item.annotation_id
                  );
                  if (res?.success && res?.data) {
                    data = res.data;
                    siblingCacheRef.current[e.item.annotation_id] = data;
                  }
                }
                if (data) {
                  // getMarkdownContent returns camelCase markdownContent
                  md = data.markdownContent || data.markdown_content || md;
                  figureUrl = data.patentImage?.patent_figure_path || "";
                  figureNumber = data.patentImage?.figure_number || figureNumber;
                  figurePageNumber =
                    data.patentImage?.figure_page_number ?? figurePageNumber;
                  figureLegend = data.patentImage?.figure_legend || figureLegend;
                  itemOriginalImagePath =
                    data.originalImagePath || itemOriginalImagePath;
                }
              } catch (err) {
                console.error("Error fetching sibling markdown content:", err);
              }
            }
            if (
              !figureUrl &&
              typeof e.item.patent_image?.patent_figure_path === "string" &&
              e.item.patent_image.patent_figure_path.startsWith("https://")
            ) {
              figureUrl = e.item.patent_image.patent_figure_path;
            }
            return {
              ...e,
              md,
              figureUrl,
              figureNumber,
              figurePageNumber,
              figureLegend,
              itemOriginalImagePath,
            };
          })
        );
        if (cancelled) return;

        const clause = pfExtractClause(
          current.item.markdown_content || enriched.find((x) => x.md)?.md || ""
        );
        const clausePos = (sf: string) => {
          const i = clause.toLowerCase().indexOf(String(sf || "").toLowerCase());
          return i < 0 ? Number.MAX_SAFE_INTEGER : i;
        };
        enriched.sort(
          (a, b) => clausePos(a.item.sub_feature) - clausePos(b.item.sub_feature)
        );

        const emphParts = pfBuildEmphParts(clause);
        const keyOf = (e: any) =>
          e.item.annotation_id || `${e.isMatched ? "m" : "n"}-${e.idx}`;
        const currentKey = keyOf(current);

        // Claim-structure hexes take precedence — indexing and vijilization
        // assign colors independently and can disagree (prod-observed).
        const claimHexEntries = pfClaimHexEntries(claimData?.selectedClaim);
        const hexFor = (item: any) =>
          pfClaimHexForSub(item?.sub_feature, claimHexEntries) ||
          pfHex(item?.element_hex_code);

        colMetaRef.current = {};
        const cols: FeatureColumnData[] = enriched.map((e) => {
          const key = keyOf(e);
          // Design items have no sub_feature — the figure label is the column.
          const eLabel = String(e.item.sub_feature || e.item.text || "");
          colMetaRef.current[key] = {
            annotationId: e.item.annotation_id,
            isMatched: e.isMatched,
            bucketIndex: e.idx,
            originalImagePath: e.itemOriginalImagePath,
            subFeature: String(e.item.sub_feature || ""),
            patentId,
            claimKey,
          };
          // Design item text IS the figure label, not analysis prose.
          let analysisHtml = "";
          if (!isDesign) {
            try {
              analysisHtml = marked.parse(
                pfEmphFig(emphParts(pfEscapeHtml(String(e.item.text || ""))))
              ) as string;
            } catch {}
          }
          let figureInsightHtml = "";
          const fi = pfExtractFigureInsight(e.md);
          if (fi) {
            try {
              figureInsightHtml = marked.parse(
                pfEmphFig(emphParts(fi))
              ) as string;
            } catch {}
          }
          return {
            key,
            subFeature: eLabel,
            isMatched: e.isMatched,
            isCurrent: key === currentKey,
            // Validated 6-digit hex only — consumers append alpha suffixes.
            hexCode: hexFor(e.item) || undefined,
            patentFigureUrl: e.figureUrl || undefined,
            figureNumber: e.figureNumber || undefined,
            figurePageNumber: e.figurePageNumber ?? undefined,
            figureLegend: legendFor(e.figureLegend, e.figureNumber),
            accusedImageUrl: pfExtractAccusedUrl(e.md) || undefined,
            chartHtml:
              pfBuildOwnChartHtml(
                e.md,
                eLabel,
                emphParts,
                key,
                hexFor(e.item),
                isDesign
              ) || undefined,
            analysisHtml: analysisHtml || undefined,
            figureInsightHtml: figureInsightHtml || undefined,
            matchPercentage: e.item.match_percentage,
          };
        });

        // Clause banner with each sub-feature highlighted (current one boxed).
        // Token pass so a replacement can't match inside earlier-inserted HTML.
        let clauseWork = pfEscapeHtml(clause ? `"${clause}"` : "");
        const tokenSpans: { token: string; span: string }[] = [];
        [...enriched]
          .sort(
            (a, b) =>
              String(b.item.sub_feature).length -
              String(a.item.sub_feature).length
          )
          .forEach((e, i) => {
            const sf = pfEscapeHtml(String(e.item.sub_feature));
            const idx = clauseWork.indexOf(sf);
            if (idx < 0) return;
            const token = `${i}`;
            const isCur = keyOf(e) === currentKey;
            // Same per-feature color as the claim-structure view (each item's
            // element_hex_code is its own sub-feature's color). data-pf-col
            // lets the feature view show that column's patent figure on hover.
            const color = hexFor(e.item) || "#1751D0";
            const noRef = !/\(\s*\d/.test(String(e.item.sub_feature));
            tokenSpans.push({
              token,
              span: `<span${
                noRef ? ' class="no-ref-text"' : ""
              } data-pf-col="${pfEscapeHtml(
                keyOf(e)
              )}" style="color:${color};font-weight:600;cursor:default;${
                isCur
                  ? `background:${color}14;border-radius:4px;padding:0 3px;outline:1px solid ${color}55;`
                  : ""
              }">${sf}</span>`,
            });
            clauseWork =
              clauseWork.slice(0, idx) + token + clauseWork.slice(idx + sf.length);
          });
        let clauseHtml = clauseWork;
        tokenSpans.forEach((t) => {
          clauseHtml = clauseHtml.replace(t.token, t.span);
        });

        const claimNumMatch = claimKey.match(/\d+/);
        const claimLabel = `Claim ${
          claimNumMatch ? claimNumMatch[0] : claimKey
        } · ${cols.length} ${isDesign ? "figure" : "feature"}${
          cols.length > 1 ? "s" : ""
        } ${isDesign ? "compared" : "in this element"}`;

        // Common claim chart: ONE table for the whole element — each feature's
        // row(s) pulled from that feature's OWN item's chart. The current
        // item's rows are highlighted.
        let commonChartHtml = "";
        const headerSource =
          enriched.find((e) => keyOf(e) === currentKey && pfParseChart(e.md)) ||
          enriched.find((e) => pfParseChart(e.md));
        const commonHeader = headerSource
          ? pfParseChart(headerSource.md)!.header
          : null;
        if (commonHeader) {
          // The Analysis column duplicates the Figure Insight minus its
          // citations — show each feature's Figure Insight there instead (its
          // [n] tokens become clickable citations via the walker effect).
          const analysisIdx = commonHeader.findIndex((h) =>
            /analysis/i.test(h)
          );
          // Canonical sub-feature name in the Feature column — generated rows
          // sometimes scope the label to one numeral ("satellite body(602)").
          const featureIdx = Math.max(
            commonHeader.findIndex((h) => /^\s*feature\s*:?\s*$/i.test(h)),
            0
          );
          const accusedTextIdx = commonHeader.findIndex((h) =>
            /accused\s*(?:instrumentality|implementation)/i.test(h)
          );
          // Design mirrors Chart View: the table shows the CURRENT figure's
          // chart only (pill switches refetch and rebuild it).
          const chartSources = isDesign
            ? enriched.filter((e) => keyOf(e) === currentKey)
            : enriched;
          const body = chartSources
            .map((e) => {
              const eLabel = String(e.item.sub_feature || e.item.text || "");
              const chart = pfParseChart(e.md);
              // Design charts already hold one row per ornamental feature.
              let rows = chart
                ? isDesign
                  ? chart.rows
                  : pfOwnChartRows(chart, e.item.sub_feature)
                : [];
              if (rows.length === 0) {
                rows = [
                  [
                    eLabel,
                    ...Array(Math.max(commonHeader.length - 1, 0)).fill("—"),
                  ],
                ];
              }
              // One table row per feature: clause-fallback items carry one
              // chart row per component — merge their distinct cells.
              if (!isDesign && rows.length > 1) {
                const merged = Array.from(
                  { length: commonHeader.length },
                  (_, ci) => {
                    const vals = Array.from(
                      new Set(
                        rows
                          .map((r) => String(r[ci] ?? "").trim())
                          .filter((v) => v && v !== "—")
                      )
                    );
                    return vals.join(" ");
                  }
                );
                rows = [merged];
              }
              const insight = pfExtractFigureInsight(e.md);
              // Design shows only the current figure — highlighting all rows
              // would just tint the whole table.
              const isCur = !isDesign && keyOf(e) === currentKey;
              return rows
                .map((r, ri) => {
                  const cells = Array.from(
                    { length: commonHeader.length },
                    (_, ci) =>
                      !isDesign && ci === featureIdx
                        ? eLabel
                        : !isDesign && ci === analysisIdx && insight
                        ? insight
                        : r[ci] ?? "—"
                  );
                  // Design rows carry their column key so the image pass puts
                  // each figure's images on its first row only ("" = skip).
                  const colAttr = isDesign
                    ? ` data-col-key="${pfEscapeHtml(
                        ri === 0 ? keyOf(e) : ""
                      )}"`
                    : "";
                  return `<tr${
                    isCur ? ' class="pf-current"' : ""
                  }${colAttr}>${cells
                    .map((c, ci) =>
                      `<td>${
                        !isDesign && ci === featureIdx
                          ? pfFeatureCellHtml(eLabel, keyOf(e), hexFor(e.item))
                          : !isDesign &&
                            !e.isMatched &&
                            ci === accusedTextIdx
                          ? pfNoMatchCellHtml(pfCellHtml(c, emphParts))
                          : pfCellHtml(c, emphParts)
                      }</td>`
                    )
                    .join("")}</tr>`;
                })
                .join("");
            })
            .join("");
          commonChartHtml = `<table><thead><tr>${commonHeader
            .map((h) => `<th>${pfCellHtml(h, emphParts)}</th>`)
            .join("")}</tr></thead><tbody>${body}</tbody></table>`;
        }

        const currentCol = cols.find((c) => c.isCurrent);

        if (!cancelled) {
          setFeatureColumnsMeta({
            clauseHtml,
            claimLabel,
            commonChartHtml,
            commonChartLabel: isDesign
              ? `Claim Chart — ${currentCol?.subFeature || "Figure"}`
              : undefined,
            currentAnalysisHtml: currentCol?.analysisHtml || "",
            currentSubFeature: currentCol?.subFeature || "",
          });
          setFeatureColumns(cols);
        }
      } catch (err) {
        console.error("Error assembling per-feature view:", err);
        if (!cancelled) setFeatureColumns(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vijilizeDetails, refreshKey, vijilizeId]);

  // Switching columns = re-pointing the stored claim context at the sibling
  // item and refetching, exactly as if it had been opened from the listing.
  const applyColumnContext = (key: string) => {
    const meta = colMetaRef.current[key];
    if (!meta) return false;
    try {
      const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
      if (!claimDataStr) return false;
      const claimData = JSON.parse(claimDataStr);
      claimData.annotationId = meta.annotationId || undefined;
      claimData.isMatchedItem = meta.isMatched;
      claimData.clickedItemIndex = meta.bucketIndex;
      claimData.subFeature = meta.subFeature || null;
      sessionStorage.setItem("vijilizeClaimData", JSON.stringify(claimData));
      return true;
    } catch {
      return false;
    }
  };

  const handleSelectColumn = (key: string) => {
    const col = (featureColumns || []).find((c) => c.key === key);
    if (!col || col.isCurrent) return;
    if (applyColumnContext(key)) {
      setIsLoading(true);
      setRefreshKey((p) => p + 1);
    }
  };

  // Annotation for a feature column: points the editor at THAT item's image,
  // coordinates and markdown (like ClauseFeatureStrip.runAnnotation), so both
  // the opened image and the save target belong to the clicked feature.
  const openFeatureAnnotation = async (key: string) => {
    const meta = colMetaRef.current[key];
    const data = meta?.annotationId
      ? siblingCacheRef.current[meta.annotationId]
      : null;
    // Without the sibling's stored context, fall back to the original flow.
    if (!meta?.originalImagePath || !data?.markdownS3Path) {
      window.handleImageEditClick?.(meta?.originalImagePath || "");
      return;
    }
    try {
      setIsAnnotationLoading(true);
      document
        .querySelectorAll(".edit-image-button")
        .forEach((b) => b.classList.add("loading"));
      const selectedTeam: any = JSON.parse(
        localStorage.getItem("selectedTeam") as string
      );
      const response: any = await copyImageForAnnotation(
        meta.originalImagePath,
        id,
        selectedTeam?.currentTeamId
      );
      const annPath = response?.data?.annotation_s3_path;
      if (!annPath) {
        toast.error("Failed to prepare image for annotation");
        return;
      }
      setAnnotationData({
        patentId: meta.patentId,
        claimKey: meta.claimKey,
        annotationId: meta.annotationId,
        isMatchedItem: meta.isMatched,
      });
      setImageCoordinates(data.imageCoordinates || null);
      setOriginalImagePath(meta.originalImagePath);
      setMarkdownS3Path(data.markdownS3Path);
      setAnnotationS3Path(annPath);
      if (data.patentImage) setPatentImage(data.patentImage);
      if (data.fontInfo) setFontInfo(data.fontInfo);
      navigate(`/project/${id}/vijilize/${vijilizeId}/annotation`);
    } catch (error) {
      console.error("Error preparing image for annotation:", error);
      toast.error("Error preparing image for annotation");
    } finally {
      setIsAnnotationLoading(false);
      document
        .querySelectorAll(".edit-image-button")
        .forEach((b) => b.classList.remove("loading"));
    }
  };

  const handleColumnAction = (key: string, type: "reanalyze" | "changeImage") => {
    const col = (featureColumns || []).find((c) => c.key === key);
    if (!col) return;
    if (col.isCurrent) {
      if (type === "reanalyze") {
        setReanalyzeSrcType("target");
        setShowReAnalyzeModal(true);
      } else {
        setShowPatentImageModal(true);
      }
      return;
    }
    // Sibling column: switch context first, open the modal once loaded.
    if (applyColumnContext(key)) {
      setPendingAction(type);
      setIsLoading(true);
      setRefreshKey((p) => p + 1);
    }
  };

  useEffect(() => {
    if (isLoading || !pendingAction) return;
    if (pendingAction === "reanalyze") {
      setReanalyzeSrcType("target");
      setShowReAnalyzeModal(true);
    } else if (pendingAction === "changeImage") {
      setShowPatentImageModal(true);
    }
    setPendingAction(null);
  }, [isLoading, pendingAction]);

  // Hovering a sub-feature mention anywhere in the details view — the clause
  // banner's colored spans (data-pf-col) or the boxed "system (100)" chips
  // (.fig-ref) in the charts/analysis text — previews that feature's patent
  // figure in a floating card. Document-level delegation because the chips
  // live inside dangerouslySetInnerHTML / injected-cell markup.
  const [featureHoverPreview, setFeatureHoverPreview] = useState<{
    key: string;
    left: number;
    top: number;
    above: boolean;
  } | null>(null);
  // Clicking a matched mention opens the figure in a modal.
  const [figureModalKey, setFigureModalKey] = useState<string | null>(null);

  useEffect(() => {
    const cols = featureColumns || [];
    if (cols.length === 0) return;
    const colFor = (el: HTMLElement) => {
      const tagged = el.closest("[data-pf-col]") as HTMLElement | null;
      if (tagged)
        return {
          span: tagged,
          col: cols.find((c) => c.key === tagged.getAttribute("data-pf-col")),
        };
      const chip = el.closest(".fig-ref") as HTMLElement | null;
      if (!chip) return null;
      // Chips carry no key — shared matcher handles scoped numerals (bare
      // figure refs like "FIG. 3A" won't match either way).
      return { span: chip, col: pfChipCol(chip.textContent || "", cols) };
    };
    const onOver = (ev: MouseEvent) => {
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      const hit = colFor(target);
      if (!hit?.col?.patentFigureUrl) {
        setFeatureHoverPreview(null);
        return;
      }
      const col = hit.col;
      hit.span.style.cursor = "zoom-in";
      const rect = hit.span.getBoundingClientRect();
      const half = 140; // popup is 280px wide — keep it inside the viewport
      const left = Math.min(
        Math.max(rect.left + rect.width / 2, half + 8),
        window.innerWidth - half - 8
      );
      const above = rect.bottom + 300 > window.innerHeight;
      const top = above ? rect.top - 8 : rect.bottom + 8;
      setFeatureHoverPreview((prev) =>
        prev &&
        prev.key === col.key &&
        prev.left === left &&
        prev.top === top &&
        prev.above === above
          ? prev
          : { key: col.key, left, top, above }
      );
    };
    const clear = () => setFeatureHoverPreview(null);
    const onClick = (ev: MouseEvent) => {
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      const hit = colFor(target);
      if (!hit?.col?.patentFigureUrl) return;
      ev.preventDefault();
      setFeatureHoverPreview(null);
      setFigureModalKey(hit.col.key);
    };
    document.addEventListener("mouseover", onOver);
    document.addEventListener("click", onClick);
    window.addEventListener("scroll", clear, true);
    return () => {
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("click", onClick);
      window.removeEventListener("scroll", clear, true);
      setFeatureHoverPreview(null);
    };
  }, [featureColumns]);

  useEffect(() => {
    if (!figureModalKey) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setFigureModalKey(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [figureModalKey]);

  const featureHoverCol = featureHoverPreview
    ? (featureColumns || []).find((c) => c.key === featureHoverPreview.key)
    : undefined;
  const figureModalCol = figureModalKey
    ? (featureColumns || []).find((c) => c.key === figureModalKey)
    : undefined;

  // Analyzer-built element profiles (patent_dict), stored by the claim view —
  // shown in the figure modal, same as the Claim Features panel's modal.
  const claimPatentDict = useMemo(() => {
    try {
      return (
        JSON.parse(sessionStorage.getItem("vijilizeClaimData") || "{}")
          ?.patentDict || null
      );
    } catch {
      return null;
    }
  }, []);

  // Same matching as ClaimElement.profilesFor: normalized patent_dict keys
  // ("handheld_device140") matched against the clicked text, keeping only the
  // most specific when one key's name is contained in another's.
  const profilesForFeature = (description: string) => {
    if (!claimPatentDict || typeof claimPatentDict !== "object") return [];
    const norm = (s: string) =>
      String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    const normDesc = norm(description);
    if (!normDesc) return [];
    const matched = Object.entries(claimPatentDict)
      .filter(
        ([key, value]) =>
          typeof value === "string" &&
          (value as string).trim() &&
          norm(key) &&
          normDesc.includes(norm(key))
      )
      .sort(([a], [b]) => norm(b).length - norm(a).length);
    const kept: [string, any][] = [];
    matched.forEach(([key, value]) => {
      if (!kept.some(([k]) => norm(k).includes(norm(key))))
        kept.push([key, value]);
    });
    return kept.map(([key, value]) => ({
      name: key.replace(/_/g, " "),
      text: String(value).trim().replace(/^"|"$/g, ""),
    }));
  };
  const figureModalProfiles = figureModalCol
    ? profilesForFeature(figureModalCol.subFeature)
    : [];

  // Chart view: color every boxed feature chip (.fig-ref) with its feature's
  // hex — same per-feature color coding as the Feature view. Matching mirrors
  // the hover preview: part number first, then name with numerals stripped.
  useEffect(() => {
    const container = contentRef.current;
    if (!container || !featureColumns?.length) return;
    ensureNoRefStyles();
    const colorFor = (text: string) =>
      pfChipCol(text, featureColumns)?.hexCode;
    const subFeatures = featureColumns.map((c) => c.subFeature);
    container.querySelectorAll<HTMLElement>(".fig-ref").forEach((chip) => {
      // Keyed chips (data-pf-col) resolve exactly — no text matching needed
      const key = chip.getAttribute("data-pf-col");
      if (!key) pfExtendChipPrefix(chip, subFeatures);
      const hex =
        (key && featureColumns.find((c) => c.key === key)?.hexCode) ||
        colorFor(chip.textContent || "");
      if (hex) {
        chip.style.color = hex;
        chip.style.background = `${hex}14`;
        chip.style.borderColor = `${hex}55`;
      }
    });

    // Unnumbered sub-features never become chips. Color the first mention of
    // each within a root (clause line, Feature / Patent Element cells) — once
    // only: the effect re-runs (image loads, cell injection) and would
    // otherwise color one more repeat mention per run.
    const unnumbered = [...featureColumns]
      .filter((c) => c.hexCode && !/\(\s*\d/.test(c.subFeature))
      .sort((a, b) => b.subFeature.length - a.subFeature.length);
    const colorMentions = (rootEl: HTMLElement) => {
      unnumbered.forEach((c) => {
        const done = Array.from(
          rootEl.querySelectorAll<HTMLElement>("[data-feat-colored]")
        ).some((s) => s.dataset.featColored === c.subFeature);
        if (done) return;
        const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT);
        const nodes: Text[] = [];
        while (walker.nextNode()) {
          const t = walker.currentNode as Text;
          const p = t.parentElement;
          if (p && (p.closest(".fig-ref") || p.closest("[data-feat-colored]")))
            continue;
          nodes.push(t);
        }
        for (const t of nodes) {
          const text = t.textContent || "";
          const idx = text.indexOf(c.subFeature);
          if (idx < 0) continue;
          const span = document.createElement("span");
          span.className = "no-ref-text";
          span.dataset.featColored = c.subFeature;
          span.dataset.pfCol = c.key;
          span.style.color = c.hexCode!;
          span.style.fontWeight = "600";
          span.textContent = c.subFeature;
          const after = t.splitText(idx);
          after.textContent = text.slice(idx + c.subFeature.length);
          t.parentNode?.insertBefore(span, after);
          break;
        }
      });
    };

    // Chart tables: a Feature cell that IS the bare feature name becomes a
    // keyed chip; classic-chart cells embed the name in the whole element
    // text, so those get first-mention coloring instead — uniform with the
    // Feature view. Patent Element cells get mention coloring too.
    const unnumberedCols = featureColumns.filter(
      (c) => !/\(\s*\d/.test(c.subFeature)
    );
    if (unnumberedCols.length) {
      container.querySelectorAll("table").forEach((tbl) => {
        const ths = Array.from(tbl.querySelectorAll("th"));
        let fi = ths.findIndex((th) =>
          /^\s*feature\s*:?\s*$/i.test(th.textContent || "")
        );
        // Column-0 fallback only for claim-chart tables — not legends etc.
        if (fi < 0 && !ths.some((th) => /accused/i.test(th.textContent || "")))
          return;
        fi = Math.max(fi, 0);
        tbl.querySelectorAll("tbody tr").forEach((tr) => {
          const td = tr.children[fi] as HTMLElement | undefined;
          if (td && !td.dataset.pfWrapped) {
            const col = td.querySelector(".fig-ref")
              ? undefined
              : pfChipCol(td.textContent || "", unnumberedCols);
            if (col) {
              td.dataset.pfWrapped = "1";
              const span = document.createElement("span");
              span.className = "fig-ref";
              // Whole-cell chip: inline-block so the bordered box can't
              // fragment into an empty second-line piece (text still wraps).
              span.style.display = "inline-block";
              span.setAttribute("data-pf-col", col.key);
              if (col.hexCode) {
                span.style.color = col.hexCode;
                span.style.background = `${col.hexCode}14`;
                span.style.borderColor = `${col.hexCode}55`;
              }
              // Edge whitespace in the chip = a break point that wraps the
              // bordered span into an empty second-line fragment.
              const isBlankText = (n: ChildNode | null) =>
                !!n && n.nodeType === Node.TEXT_NODE && !n.textContent?.trim();
              while (isBlankText(td.firstChild)) td.removeChild(td.firstChild!);
              while (isBlankText(td.lastChild)) td.removeChild(td.lastChild!);
              while (td.firstChild) span.appendChild(td.firstChild);
              td.appendChild(span);
            } else {
              colorMentions(td);
            }
          }
          // Every other text cell too — injected image cells host React
          // portals and must not be mutated.
          Array.from(tr.children).forEach((cell, ci) => {
            const el = cell as HTMLElement;
            if (ci === fi || el.dataset.pfImgCell) return;
            colorMentions(el);
          });
        });
      });
    }

    // Clause line under "Feature:" — same first-mention coloring.
    const h4 = Array.from(container.querySelectorAll<HTMLElement>("h4")).find(
      (h) => /^\s*feature\s*:/i.test(h.textContent || "")
    );
    const clauseEl =
      h4 &&
      (h4.textContent || "").replace(/^\s*feature\s*:/i, "").trim().length > 0
        ? h4
        : ((h4?.nextElementSibling as HTMLElement | null) || null);
    if (clauseEl) colorMentions(clauseEl);
  }, [
    htmlContent,
    featureColumns,
    detailsViewMode,
    insightSwapKey,
    chartImageCells,
    featureChartImageCells,
    imagesLoaded,
  ]);

  useEffect(() => {
    if (!htmlContent || !contentRef.current) return;

    const images = contentRef.current.querySelectorAll("img.markdown-image");
    setImagesCount(images.length);

    if (images.length === 0) {
      setImagesLoaded(true);
      return;
    }

    const handleImageLoad = () => {
      setLoadedImagesCount((prevCount) => {
        const newCount = prevCount + 1;
        if (newCount >= images.length) {
          setImagesLoaded(true);
        }
        return newCount;
      });
    };

    const handleImageError = () => {
      handleImageLoad();
    };

    images.forEach((img) => {
      const imgElement = img as HTMLImageElement;

      if (imgElement.complete) {
        handleImageLoad();
      } else {
        imgElement.addEventListener("load", handleImageLoad);
        imgElement.addEventListener("error", handleImageError);
      }
    });

    return () => {
      images.forEach((img) => {
        const imgElement = img as HTMLImageElement;
        imgElement.removeEventListener("load", handleImageLoad);
        imgElement.removeEventListener("error", handleImageError);
      });
    };
  }, [htmlContent]);

  return (
    <>
      <div className="dashboard-wrapper flex">
        <Sidebar />
        <div className="w-[calc(100%_-_72px)] ml-auto">
          <Header title="Vijilize" count={0} isChildEle={true} type="project" />
          <div className="pt-4 px-4 h-[calc(100vh_-_69px)] overflow-y-auto scrollbar-adjust bg-[#f9fafb]">
            <div className="bg-white rounded-lg shadow-customShadow5 p-8 w-[90%] max-w-[1600px] mx-auto">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-xl font-interSemiBold text-[#1A202C]">
                  Detailed Analysis
                </h1>
                <div className="flex items-center gap-x-4">
                  {featureColumns && featureColumns.length > 0 && (
                    <div className="flex items-center">
                      <button
                        onClick={() => setDetailsViewMode("classic")}
                        className={`text-sm font-interMedium py-1.5 px-3 border rounded-tl-lg rounded-bl-lg ${
                          detailsViewMode === "classic"
                            ? "border-[#1751D0] text-[#1751D0] bg-[#F5F8FD]"
                            : "border-[#E6E6E6] text-[#4F4D55] bg-white"
                        }`}
                      >
                        Chart View
                      </button>
                      <button
                        onClick={() => setDetailsViewMode("graph")}
                        className={`text-sm font-interMedium py-1.5 px-3 border border-l-0 rounded-tr-lg rounded-br-lg ${
                          detailsViewMode === "graph"
                            ? "border-[#1751D0] border-l text-[#1751D0] bg-[#F5F8FD]"
                            : "border-[#E6E6E6] text-[#4F4D55] bg-white"
                        }`}
                      >
                        Feature View
                      </button>
                    </div>
                  )}
                  {citationsData.length > 0 && (
                    <button
                      onClick={() => {
                        if (!selectedCitation && citationsData.length > 0) {
                          setSelectedCitation({
                            value: citationsData[0].number,
                            label: `${citationsData[0].number}`,
                          });
                        }
                        setCitationsTab("citations");
                        setIsShowSideBarCitations(true);
                      }}
                      className="text-sm text-[#1751D0] font-interSemiBold hover:underline"
                    >
                      View Citations
                    </button>
                  )}
                  {partsListData.length > 0 && (
                    <button
                      onClick={() => {
                        setCitationsTab("parts");
                        setIsShowSideBarCitations(true);
                      }}
                      className="text-sm text-[#1751D0] font-interSemiBold hover:underline"
                    >
                      Parts List
                    </button>
                  )}
                  <button
                    onClick={() => navigate(-1)}
                    className="flex items-center text-sm text-[#1751D0] font-interMedium hover:underline"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                    Back
                  </button>
                </div>
              </div>

            {isLoading ? (
              <div className="h-64">
                <div className="fixed inset-0 z-10 flex justify-center items-center pointer-events-none">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              </div>
            ) : error ? (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
                role="alert"
              >
                <strong className="font-bold">Error: </strong>
                <span className="block sm:inline">{error}</span>
              </div>
            ) : !imagesLoaded && imagesCount > 0 ? (
              <div className="animate-pulse">
                <div className="h-7 bg-[#F2F4F7] rounded-md w-3/4 mb-5"></div>
                <div className="h-5 bg-[#F2F4F7] rounded-md w-full mb-3"></div>
                <div className="h-5 bg-[#F2F4F7] rounded-md w-5/6 mb-3"></div>
                <div className="h-5 bg-[#F2F4F7] rounded-md w-4/6 mb-5"></div>
                <div className="h-72 bg-[#F2F4F7] rounded-md w-full mb-5 border border-[#E5E7EB]"></div>
                <div className="h-5 bg-[#F2F4F7] rounded-md w-full mb-3"></div>
                <div className="h-5 bg-[#F2F4F7] rounded-md w-5/6 mb-3"></div>
                <div className="h-8 bg-[#F2F4F7] rounded-md w-3/5 mb-4 mt-8"></div>
                <div className="h-40 bg-[#F2F4F7] rounded-md w-full mb-5 border border-[#E5E7EB]"></div>
              </div>
            ) : (
              <div key={refreshKey} ref={contentRef}>
                {featureColumns &&
                featureColumns.length > 0 &&
                detailsViewMode === "graph" ? (
                  /* Per-feature column view: current item's own analysis plus
                     sibling sub-features pulled from their own items */
                  <PerFeatureAnalysisView
                    claimLabel={featureColumnsMeta.claimLabel}
                    clauseHtml={featureColumnsMeta.clauseHtml}
                    commonChartHtml={featureColumnsMeta.commonChartHtml}
                    commonChartLabel={featureColumnsMeta.commonChartLabel}
                    currentAnalysisHtml={featureColumnsMeta.currentAnalysisHtml}
                    currentSubFeature={featureColumnsMeta.currentSubFeature}
                    columns={featureColumns}
                    expandedKey={expandedColumnKey}
                    onToggleExpand={(key) =>
                      setExpandedColumnKey((prev) =>
                        key === null || prev === key ? null : key
                      )
                    }
                    onSelectColumn={handleSelectColumn}
                    onChangePatentImage={(key) =>
                      handleColumnAction(key, "changeImage")
                    }
                    onAnnotate={openFeatureAnnotation}
                    onReanalyze={(key) => handleColumnAction(key, "reanalyze")}
                    patentLink={patentPdfLink}
                    targetLink={targetDocLink}
                    targetSnapshotLink={targetSnapshotUrl}
                    onImageRotated={handleImageRotated}
                  />
                ) : featureColumns === undefined &&
                  detailsViewMode === "graph" &&
                  sessionStorage.getItem("vijilizeClaimData") ? (
                  /* Sibling columns still assembling */
                  <div className="relative">
                    <div className="fixed inset-0 z-10 flex items-center justify-center pointer-events-none">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                    <div className="animate-pulse">
                      <div className="h-16 bg-[#F2F4F7] rounded-xl w-3/5 mx-auto mb-4"></div>
                      <div className="h-8 bg-[#F2F4F7] rounded-full w-2/5 mx-auto mb-5"></div>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                        <div className="h-96 bg-[#F2F4F7] rounded-xl border border-[#E5E7EB]"></div>
                        <div className="h-96 bg-[#F2F4F7] rounded-xl border border-[#E5E7EB]"></div>
                        <div className="h-96 bg-[#F2F4F7] rounded-xl border border-[#E5E7EB]"></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* 1. Claim chart — scrolls horizontally when the injected
                        image + legend columns outgrow the card */}
                    <div
                      className="markdown-content prose max-w-none overflow-x-auto"
                      dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />

                    {/* Bottom Patent Figure | Accused Instrumentality card
                        removed — the chart's injected image columns show the
                        same images per feature row. */}

                    {/* 3. Figure insight — only when it couldn't be folded
                        into the chart's Analysis column (no sibling data) */}
                    {figureInsightHtml &&
                      !(featureColumns && featureColumns.length > 0) && (
                        <div
                          className="markdown-content prose max-w-none mt-4 border-t pt-6"
                          dangerouslySetInnerHTML={{ __html: figureInsightHtml }}
                        />
                      )}
                  </>
                )}

                {/* Snapshot disclaimer for website targets — shown at the
                    bottom of BOTH Chart and Feature views (hidden while the
                    feature columns are still assembling) */}
                {targetSnapshotDate &&
                  !(
                    featureColumns === undefined &&
                    detailsViewMode === "graph" &&
                    sessionStorage.getItem("vijilizeClaimData")
                  ) && (
                    <p className="mt-6 pt-3 border-t border-gray-100 text-[11px] text-[#98A2B3] text-center">
                      Target website snapshot as of{" "}
                      {formatDate(targetSnapshotDate)} — this analysis reflects
                      the site content captured on that date.
                    </p>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Zoomable images for the injected classic-chart columns — each row uses
        its own feature's images (siblings differ), falling back to the current
        item's images when no feature column matches. */}
    {chartImageCells.patent.map((cell, i) =>
      createPortal(
        <div>
          <div className="min-w-[13rem]">
            <ExpandableZoomableImage
              url={
                resolveRowFeatureColumn(cell.feature)?.patentFigureUrl ||
                patentImage?.patent_figure_path ||
                ""
              }
              alt="Patent Figure"
              title={cell.feature ? `Patent Figure — ${cell.feature}` : "Patent Figure"}
              link={withPage(
                patentPdfLink,
                resolveRowFeatureColumn(cell.feature)?.figurePageNumber ??
                  patentImage?.figure_page_number
              )}
              linkLabel="View Patent"
              height="12rem"
              legend={patentCellLegend(cell.feature)}
              onRotated={handleImageRotated}
            />
          </div>
          {legendListEl(patentCellLegend(cell.feature))}
        </div>,
        cell.el,
        `chart-col-patent-${i}`
      )
    )}
    {chartImageCells.accused.map((cell, i) =>
      createPortal(
        <ExpandableZoomableImage
          url={
            resolveRowFeatureColumn(cell.feature)?.accusedImageUrl ||
            accusedImageUrl ||
            ""
          }
          alt="Accused Instrumentality"
          title={
            cell.feature
              ? `Accused Image — ${cell.feature}`
              : "Accused Image"
          }
          link={withPage(
            targetDocLink,
            isPdfUrl(targetDocLink)
              ? pageFromImageUrl(
                  resolveRowFeatureColumn(cell.feature)?.accusedImageUrl ||
                    accusedImageUrl
                )
              : null
          )}
          linkLabel="View Target"
          snapshotLink={withPage(
            targetSnapshotUrl,
            pageFromImageUrl(
              resolveRowFeatureColumn(cell.feature)?.accusedImageUrl ||
                accusedImageUrl
            )
          )}
          height="12rem"
          onRotated={handleImageRotated}
          onAnnotate={() => {
            const c = resolveRowFeatureColumn(cell.feature);
            if (c) openFeatureAnnotation(c.key);
            else window.handleImageEditClick?.("");
          }}
          noMatchBadge={
            accusedNoMatch(cell.feature) ||
            !pfIsAnnotatedImgUrl(
              resolveRowFeatureColumn(cell.feature)?.accusedImageUrl ||
                accusedImageUrl
            )
          }
        />,
        cell.el,
        `chart-col-accused-${i}`
      )
    )}

    {/* Same, for the Feature View's common claim chart */}
    {featureChartImageCells.patent.map((cell, i) => {
      const cellCol = cell.colKey
        ? (featureColumns || []).find((c) => c.key === cell.colKey)
        : resolveRowFeatureColumn(cell.feature);
      return createPortal(
        <div>
          <div className="min-w-[13rem]">
            <ExpandableZoomableImage
              url={
                cellCol?.patentFigureUrl ||
                patentImage?.patent_figure_path ||
                ""
              }
              alt="Patent Figure"
              title={cell.feature ? `Patent Figure — ${cell.feature}` : "Patent Figure"}
              link={withPage(
                patentPdfLink,
                cellCol?.figurePageNumber ?? patentImage?.figure_page_number
              )}
              linkLabel="View Patent"
              height="12rem"
              legend={patentCellLegend(cell.feature)}
              onRotated={handleImageRotated}
            />
          </div>
          {legendListEl(patentCellLegend(cell.feature))}
        </div>,
        cell.el,
        `feature-chart-col-patent-${i}`
      );
    })}
    {featureChartImageCells.accused.map((cell, i) => {
      const cellCol = cell.colKey
        ? (featureColumns || []).find((c) => c.key === cell.colKey)
        : resolveRowFeatureColumn(cell.feature);
      return createPortal(
        <ExpandableZoomableImage
          url={cellCol?.accusedImageUrl || accusedImageUrl || ""}
          alt="Accused Instrumentality"
          title={
            cell.feature
              ? `Accused Image — ${cell.feature}`
              : "Accused Image"
          }
          link={withPage(
            targetDocLink,
            isPdfUrl(targetDocLink)
              ? pageFromImageUrl(cellCol?.accusedImageUrl || accusedImageUrl)
              : null
          )}
          linkLabel="View Target"
          snapshotLink={withPage(
            targetSnapshotUrl,
            pageFromImageUrl(cellCol?.accusedImageUrl || accusedImageUrl)
          )}
          height="12rem"
          onRotated={handleImageRotated}
          onAnnotate={() =>
            cellCol
              ? openFeatureAnnotation(cellCol.key)
              : window.handleImageEditClick?.("")
          }
          noMatchBadge={
            (cellCol ? !cellCol.isMatched : accusedNoMatch(cell.feature)) ||
            !pfIsAnnotatedImgUrl(cellCol?.accusedImageUrl || accusedImageUrl)
          }
        />,
        cell.el,
        `feature-chart-col-accused-${i}`
      );
    })}

    {/* Patent-figure preview for the hovered sub-feature mention */}
    {featureHoverPreview &&
      featureHoverCol?.patentFigureUrl &&
      createPortal(
        <div
          className="fixed pointer-events-none"
          style={{
            left: featureHoverPreview.left,
            top: featureHoverPreview.top,
            transform: `translate(-50%, ${
              featureHoverPreview.above ? "-100%" : "0"
            })`,
            zIndex: 1300,
          }}
        >
          <div className="w-[280px] bg-white border border-gray-200 rounded-lg shadow-xl p-2">
            <img
              src={featureHoverCol.patentFigureUrl}
              alt={featureHoverCol.subFeature}
              className="w-full max-h-[220px] object-contain bg-[#FAFBFC] rounded"
            />
            <p className="mt-1.5 text-center text-[11px] font-interSemiBold truncate">
              <span
                style={
                  featureHoverCol.hexCode
                    ? { color: featureHoverCol.hexCode }
                    : undefined
                }
              >
                {featureHoverCol.subFeature}
              </span>
              {featureHoverCol.figureNumber && (
                <span className="text-[#667085] font-interMedium">
                  {" · "}
                  {featureHoverCol.figureNumber.replace(/_/g, ". ")}
                </span>
              )}
            </p>
          </div>
        </div>,
        document.body
      )}

    {/* Full figure modal for a clicked sub-feature mention */}
    {figureModalCol?.patentFigureUrl &&
      createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 1250 }}
          onClick={() => setFigureModalKey(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
            style={{ width: "70vw", maxWidth: "980px", maxHeight: "82vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
              <span className="text-[14px] font-interSemiBold">
                <span
                  style={
                    figureModalCol.hexCode
                      ? { color: figureModalCol.hexCode }
                      : { color: "#1A202C" }
                  }
                >
                  {figureModalCol.subFeature}
                </span>
                {figureModalCol.figureNumber && (
                  <span className="text-[#667085] font-interMedium">
                    {" · "}
                    {figureModalCol.figureNumber.replace(/_/g, ". ")}
                  </span>
                )}
              </span>
              <button
                type="button"
                title="Close"
                onClick={() => setFigureModalKey(null)}
                className="p-1 rounded hover:bg-gray-100 text-[#667085]"
                style={{ cursor: "pointer" }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3">
              <div className="flex gap-4">
                <div className="flex-1 min-w-0 bg-[#FAFBFC] rounded-md overflow-hidden">
                  <ZoomableImage
                    url={figureModalCol.patentFigureUrl}
                    alt={figureModalCol.subFeature}
                    height={figureModalProfiles.length > 0 ? "56vh" : "66vh"}
                  />
                </div>
                {figureModalCol.figureLegend &&
                  figureModalCol.figureLegend.length > 0 && (
                    <ul className="w-64 flex-shrink-0 overflow-y-auto space-y-1.5 pl-4 border-l border-dashed border-gray-200 text-left [scrollbar-width:thin]">
                      {figureModalCol.figureLegend.map((legend, index) => {
                        const k = Object.keys(legend)[0];
                        return (
                          <li
                            key={index}
                            className="text-[13px] leading-snug text-gray-800"
                          >
                            {k}. {legend[k]}
                          </li>
                        );
                      })}
                    </ul>
                  )}
              </div>

              {/* Analyzer-built element profile(s) — same as the Claim
                  Features panel's modal */}
              {figureModalProfiles.length > 0 && (
                <div className="mt-4 border border-gray-200 rounded-lg bg-[#F8FAFC] px-4 py-3">
                  <p className="text-[11px] font-interSemiBold uppercase tracking-wide text-[#667085] mb-2">
                    Element Profile
                  </p>
                  <div className="space-y-3">
                    {figureModalProfiles.map((profile) => (
                      <div key={profile.name}>
                        <p className="text-sm font-interSemiBold text-[#1A202C] capitalize">
                          {profile.name}
                        </p>
                        <p className="text-sm text-[#475569] leading-relaxed">
                          {profile.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

    <ReAnalyzeModal
      isShow={showReAnalyzeModal}
      setIsShow={setShowReAnalyzeModal}
      vijilizeId={vijilizeId}
      targetPatentId={(() => {
        const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
        if (claimDataStr) {
          try {
            const claimData = JSON.parse(claimDataStr);
            // "source" type (Patent Image) → use source patent (activeIndex.patentId)
            // "target" type (Accused Instrumentality) → use target patent (targetDocumentId)
            if (reanalyzeSrcType === "source") {
              return claimData.activeIndex?.patentId;
            }
            return claimData.targetDocumentId || claimData.activeIndex?.patentId;
          } catch (e) {
            return undefined;
          }
        }
        return undefined;
      })()}
      projectId={id}
      imageS3Path={fetchedMarkdownContentData?.markdownS3Path || markdownS3Path || undefined}
      onReanalyze={async (instruction, newImageUrl) => {
        try {
          // Get claim data from sessionStorage first
          const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
          if (!claimDataStr) {
            toast.error("Missing claim data. Please try again.");
            return;
          }

          const claimData = JSON.parse(claimDataStr);
          const { activeIndex, selectedClaim, selectedSimilarity } = claimData;

          // Get team ID from localStorage
          const selectedTeam: any = JSON.parse(
            localStorage.getItem("selectedTeam") as string
          );
          const teamId: string = selectedTeam?.currentTeamId;

          if (!id || !teamId) {
            toast.error("Missing project or team information.");
            return;
          }

          // Get the actual comparison ID
          // Priority: claimData > actualComparisonId state > vijilizeId from URL
          let comparisonIdToUse = vijilizeId;
          
          console.log("=== Comparison ID Resolution ===");
          console.log("vijilizeId from URL params:", vijilizeId);
          console.log("actualComparisonId from state:", actualComparisonId);
          console.log("claimData:", claimData);
          
          // Try to get the comparison ID from claimData if available
          if (claimData.comparisonId) {
            comparisonIdToUse = claimData.comparisonId;
            console.log("Using comparisonId from claimData:", comparisonIdToUse);
          } else if (actualComparisonId) {
            comparisonIdToUse = actualComparisonId;
            console.log("Using actualComparisonId from state:", comparisonIdToUse);
          } else {
            console.log("Using vijilizeId from URL params:", comparisonIdToUse);
          }
          
          console.log("Final comparison ID for status update:", comparisonIdToUse);
          console.log("================================");

          // Show toast and navigate immediately (don't wait for status update)
          toast.success("Reanalysis in progress… please wait a moment.");
          
          // Navigate to Vijilizations tab immediately with the vijilization ID to update
          navigate(`/project/${id}/find-covered-product`, {
            state: {
              reanalysisStarted: true,
              activeSection: 1, // Set to Vijilizations tab
              forceRefresh: true,
              timestamp: Date.now(),
              optimisticStatusUpdate: {
                vijilizationId: comparisonIdToUse,
                status: "in_progress"
              }
            },
          });

          // Update vijilization status to "in_progress" in the background (fire and forget)
          if (comparisonIdToUse) {
            updateVijilizationStatus(comparisonIdToUse, "in_progress").catch(error => {
              console.error("Error updating vijilization status:", error);
              // Status update failed, but navigation already happened
            });
          }

          // Continue reanalysis in background (fire and forget)
          // We wrap this in a self-executing async function to not block navigation
          (async () => {
            try {
              setIsVijilizing(true);

          // Extract claim_preamble text
          let claimPreambleText = "";
          if (selectedClaim?.claim_preamble || selectedClaim?.preamble) {
            const preambleData = selectedClaim?.claim_preamble || selectedClaim?.preamble;
            if (Array.isArray(preambleData)) {
              claimPreambleText = preambleData.map((p: any) => p.description || p).join(" ");
            } else {
              claimPreambleText = preambleData;
            }
          }

          // Extract sourcePatentId from patent_figure_path
          let sourcePatentId = "";
          if (patentImage?.patent_figure_path) {
            // Extract from URL: https://bucket.s3.region.amazonaws.com/sourcePatentId/projectId/figures/...
            const pathMatch = patentImage.patent_figure_path.match(/\/([^\/]+)\/([^\/]+)\/figures\//);
            if (pathMatch && pathMatch[1]) {
              sourcePatentId = pathMatch[1];
            }
          }

          // Fallback to activeIndex.patentId if extraction fails
          if (!sourcePatentId) {
            sourcePatentId = activeIndex.patentId;
          }

          // Extract targetPatentId from targetPatentFigureS3Path
          let targetPatentId = activeIndex.patentId; // Default fallback
          const targetImagePath = newImageUrl || originalImagePath || "";
          
          if (targetImagePath) {
            // Try to extract from URL pattern: .../targetPatentId/projectId/file/...
            // or from presigned URL: https://bucket.s3.region.amazonaws.com/targetPatentId/projectId/file/...
            const targetMatch = targetImagePath.match(/\/([^\/]+)\/([^\/]+)\/file\//);
            if (targetMatch && targetMatch[1]) {
              targetPatentId = targetMatch[1];
            }
          }

          // Get claim element text (the full clause) - try multiple sources
          let claimElement = "";
          if (selectedSimilarity?.description) {
            claimElement = selectedSimilarity.description;
          } else if (selectedClaim?.description) {
            claimElement = selectedClaim.description;
          } else if (fetchedMarkdownContentData?.markdownContent || fetchedMarkdownContentData?.markdown_content) {
            // Try to extract from markdown content
            const markdownText = fetchedMarkdownContentData?.markdownContent || fetchedMarkdownContentData?.markdown_content;
            
            // Method 1: Extract from "#### Feature:" section
            let featureMatch = markdownText.match(/####\s*Feature:\s*\n(.+?)(?:\n\n|\n\||\||$)/s);
            if (featureMatch && featureMatch[1]) {
              claimElement = featureMatch[1].trim();
            } else {
              // Method 2: Extract from heading (##) 
              const headingMatch = markdownText.match(/##\s*(.+?)(?:\n|$)/);
              if (headingMatch && headingMatch[1]) {
                claimElement = headingMatch[1].trim();
              } else {
                // Method 3: Extract first non-empty paragraph after any heading
                const paragraphMatch = markdownText.match(/(?:^|\n)([A-Z][^\n]+(?:design|claim|element|feature)[^\n]+)(?:\n|$)/i);
                if (paragraphMatch && paragraphMatch[1]) {
                  claimElement = paragraphMatch[1].trim();
                }
              }
            }
          }

          // claimFeature = the specific sub-feature the annotation model
          // highlights — never the whole clause. The markdown's "#### Feature:"
          // section holds the clause; the sub-feature lives in the chart
          // table's Feature column, so that's the deep-link fallback.
          let claimFeatureText =
            claimData.subFeature || featureColumnsMeta?.currentSubFeature || "";
          if (!claimFeatureText) {
            const chartMd =
              fetchedMarkdownContentData?.markdownContent ||
              fetchedMarkdownContentData?.markdown_content ||
              "";
            for (const line of String(chartMd).split("\n")) {
              if (!line.trim().startsWith("|")) continue;
              const cell = (line.split("|")[1] || "").trim();
              if (!cell || /^feature$/i.test(cell) || /^:?-{2,}:?$/.test(cell))
                continue;
              claimFeatureText = cell.replace(/^\[(.*)\]$/, "$1").trim();
              break;
            }
          }
          if (!claimFeatureText) claimFeatureText = claimElement;

          console.log("Debug - Extracted data:", {
            sourcePatentId,
            targetPatentId,
            claimElement,
            claimPreambleText,
            claimFeatureText,
            patentImage,
            selectedClaim,
            selectedSimilarity,
            targetImagePath,
          });

          // Prepare the payload for reanalysis
          const payload = {
            sourcePatentId: sourcePatentId,
            targetPatentId: targetPatentId,
            claimElement: claimElement,
            sourcePatentFigureS3Path: fetchedMarkdownContentData?.patentFigureS3Path || (() => {
              const presignedUrl = patentImage?.patent_figure_path;
              if (!presignedUrl || !presignedUrl.startsWith("https://")) return "";
              try {
                const url = new URL(presignedUrl);
                const bucket = url.hostname.split(".")[0];
                const key = url.pathname.substring(1);
                return `s3://${bucket}/${key}`;
              } catch { return ""; }
            })(),
            targetPatentFigureS3Path: newImageUrl || originalImagePath || "",
            projectId: id,
            teamId: teamId,
            instruction: instruction || undefined,
            claimPreamble: claimPreambleText,
            claimFeature: claimFeatureText,
          };

          console.log("Reanalyze payload:", payload);

          // Call the reanalysis API
          const response = await reanalyzeSingleClaimElement(payload);
          
          console.log("Reanalysis response:", response);
          console.log("Reanalysis response.image_coordinates:", response?.image_coordinates);
          console.log("Reanalysis response.result:", response?.result);
          console.log("Reanalysis response.result?.image_coordinates:", response?.result?.image_coordinates);
          
          // Save the reanalysis result to the database first
          if (response) {
            // Handle response structure - check if data is nested in 'result' property
            const resultData = response.result || response;
            
            const storedClaimKey = claimData.storedClaimKey || `claim${activeIndex.claimIndex + 1}`;
            
            // Use annotation_id from response if available, otherwise use the one from claimData
            const annotationIdToUse = resultData.annotation_id || response.annotation_id || claimData.annotationId || undefined;
            
            console.log("Using annotation ID:", annotationIdToUse);
            console.log("Response annotation_id:", response.annotation_id);
            console.log("ClaimData annotation_id:", claimData.annotationId);
            console.log("Result data image_coordinates:", resultData.image_coordinates);
            
            // STEP 1: Call update-citations-for-single-comparison API to get updated analysis
            // We only need to replace the analysis field, keep everything else from resultData
            try {
              console.log("Calling update-citations-for-single-comparison API...");
              console.log("Original analysis:", resultData.analysis || response.analysis);
              
              const citationsPayload = {
                index_name: teamId,
                result: {
                  analysis: resultData.analysis || response.analysis || "",
                  is_match: resultData.is_match !== undefined ? resultData.is_match : response.is_match,
                  match_percentage: resultData.match_percentage !== undefined ? resultData.match_percentage : response.match_percentage,
                  markdown_content: resultData.markdown_content || response.markdown_content || "",
                  markdown_image: resultData.original_image_path || response.original_image_path || "",
                  original_image_path: resultData.original_image_path || response.original_image_path || "",
                  image_coordinates: resultData.image_coordinates || response.image_coordinates || [],
                  annotation_id: annotationIdToUse,
                  is_system_generated: resultData.is_system_generated !== undefined ? resultData.is_system_generated : response.is_system_generated,
                  patent_figure_path: resultData.patent_figure_path || response.patent_figure_path || "",
                  figure_legend: resultData.figure_legend || response.figure_legend || [],
                  figure_number: resultData.figure_number || response.figure_number || "",
                  features: resultData.features || response.features || [],
                  citations: resultData.citations || response.citations || [],
                  total_cost: resultData.total_cost !== undefined ? resultData.total_cost : response.total_cost,
                  hex_code: resultData.hex_code || response.hex_code || "#FF0000",
                },
                team_id: teamId,
                vijilize_id: comparisonIdToUse || "",
              };
              
              const citationsResponse = await updateCitationsForSingleComparison(citationsPayload);
              
              console.log("Citations API response:", citationsResponse);
              
              // ONLY replace analysis and citations from the response, keep everything else from resultData
              if (citationsResponse && citationsResponse.updated_analysis) {
                resultData.analysis = citationsResponse.updated_analysis;
                console.log("✅ Replaced analysis with updated_analysis from citations API");
                console.log("Updated analysis:", resultData.analysis);
              }
              
              if (citationsResponse && citationsResponse.new_citations) {
                resultData.citations = citationsResponse.new_citations;
                console.log("✅ Replaced citations with new_citations from citations API");
                console.log("New citations count:", resultData.citations.length);
                console.log("Footer map:", citationsResponse.footer_map);
              }
            } catch (citationsError) {
              console.error("Error calling update-citations-for-single-comparison:", citationsError);
              // Continue with original analysis if citations API fails
              console.log("Continuing with original analysis due to citations API error");
            }
            
            // STEP 2: Call updateReanalysisResult with resultData (which now has updated analysis)
            await updateReanalysisResult({
              annotationId: annotationIdToUse,
              comparisonId: comparisonIdToUse || "",
              patentId: activeIndex.patentId,
              claimKey: storedClaimKey,
              isMatchedItem: claimData.isMatchedItem,
              markdownContent: resultData.markdown_content || response.markdown_content,
              analysis: resultData.analysis || response.analysis || "",
              originalImagePath: resultData.original_image_path || response.original_image_path,
              patentFigurePath: resultData.patent_figure_path || response.patent_figure_path,
              figureNumber: resultData.figure_number || response.figure_number,
              figurePageNumber: resultData.figure_page_number ?? response.figure_page_number ?? null,
              isMatch: resultData.is_match !== undefined ? resultData.is_match : response.is_match,
              matchPercentage: resultData.match_percentage !== undefined ? resultData.match_percentage : response.match_percentage,
              citations: resultData.citations || response.citations,
              features: resultData.features || response.features,
              figureLegend: resultData.figure_legend || response.figure_legend,
              hexCode: resultData.hex_code || response.hex_code,
              isSystemGenerated: resultData.is_system_generated !== undefined ? resultData.is_system_generated : response.is_system_generated,
              totalCost: resultData.total_cost !== undefined ? resultData.total_cost : response.total_cost,
              imageCoordinates: resultData.image_coordinates || response.image_coordinates,
              projectId: id,
              teamId: teamId,
              clickedItemIndex: claimData.clickedItemIndex,
              targetDocumentId: claimData.targetDocumentId || undefined,
            });
            
            // Reanalysis completed successfully
            console.log("Reanalysis completed successfully");
            
            // Update status back to completed (backend should handle this, but we do it as backup)
            if (comparisonIdToUse) {
              try {
                await updateVijilizationStatus(comparisonIdToUse, "completed");
              } catch (error) {
                console.error("Error updating vijilization status to completed:", error);
              }
            }
            
            // Trigger a page refresh to show the updated status
            // Navigate to the same page with a refresh flag
            navigate(`/project/${id}/find-covered-product`, {
              state: {
                reanalysisComplete: true,
                activeSection: 1, // Vijilizations tab
                forceRefresh: true,
                timestamp: Date.now(),
                optimisticStatusUpdate: {
                  vijilizationId: comparisonIdToUse,
                  status: "completed"
                }
              },
              replace: true, // Replace current history entry
            });
          }
        } catch (error: any) {
          console.error("Error during background reanalysis:", error);
          toast.error("ReAnalyze failed. The original analysis is unchanged.");
          // A failed SINGLE-ELEMENT reanalysis must not mark the whole
          // comparison failed — the original completed results are intact,
          // and a "failed" status blocks every page (vijilize-details only
          // serves status=completed comparisons).
          if (comparisonIdToUse) {
            try {
              await updateVijilizationStatus(comparisonIdToUse, "completed");
            } catch (statusError) {
              console.error("Error restoring vijilization status:", statusError);
            }
          }
        } finally {
          setIsVijilizing(false);
        }
      })(); // Execute the async function immediately

        } catch (error: any) {
          console.error("Error initiating reanalysis:", error);
          toast.error(error?.message || "Failed to start reanalysis. Please try again.");
        }
      }}
    />
    
    {/* Patent Image selector — uses figures/ folder from patent_figure_path */}
    {showPatentImageModal && (
      <ChangeVijilizeImageModal
        isShow={showPatentImageModal}
        setIsShow={setShowPatentImageModal}
        vijilizeId={vijilizeId}
        folderType="figures"
        imageS3Path={(() => {
          const rawPath = fetchedMarkdownContentData?.patentFigureS3Path;
          if (rawPath?.startsWith("s3://")) return rawPath;
          return undefined;
        })()}
        targetPatentId={(() => {
          const isMongoId = (val?: string) => !!val && /^[a-f\d]{24}$/i.test(val);
          // Extract source patent ID from the figures S3 path: s3://bucket/{patentId}/{projectId}/figures/...
          const rawPath = fetchedMarkdownContentData?.patentFigureS3Path;
          if (rawPath?.startsWith("s3://")) {
            const parts = rawPath.substring(5).split("/");
            const pid = parts[1]; // bucket / patentId / projectId / figures / ...
            if (isMongoId(pid)) return pid;
          }
          // Fall back to sessionStorage sourcePatentId
          try {
            const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
            if (claimDataStr) {
              const pid = JSON.parse(claimDataStr).sourcePatentId
                || JSON.parse(claimDataStr).activeIndex?.patentId;
              if (isMongoId(pid)) return pid;
            }
          } catch (e) {}
          return undefined;
        })()}
        projectId={id}
        onImageChanged={async (newImageUrl, previewUrl) => {
          const displayUrl = previewUrl || newImageUrl;
          let annotationId: string | undefined;
          let patentId: string | undefined;
          let claimKey: string | undefined;
          try {
            const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
            if (claimDataStr) {
              const claimData = JSON.parse(claimDataStr);
              annotationId = claimData.annotationId;
              patentId = claimData.activeIndex?.patentId;
              claimKey = claimData.storedClaimKey || `claim${(claimData.activeIndex?.claimIndex || 0) + 1}`;
            }
          } catch (e) {}

          setShowPatentImageModal(false);
          setPatentImageSaving(true);

          // Persist and preload in parallel: the old figure stays visible
          // under the loader until the new one is downloaded AND saved, so
          // the swap is instant instead of flashing a half-loaded image.
          const preload = new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = displayUrl;
          });
          let persisted = false;
          try {
            await Promise.all([
              changePatentImage({
                comparison_id: vijilizeId || "",
                patent_figure_s3_path: newImageUrl,
                annotation_id: annotationId,
                patent_id: patentId,
                claim_key: claimKey,
              }),
              preload,
            ]);
            persisted = true;
          } catch (error) {
            console.error("Error persisting patent image change:", error);
            toast.error("Failed to save patent image. Please try again.");
          }

          if (persisted) {
            setPatentImage({ patent_figure_path: displayUrl });
            // Graph view renders the COLUMN's figure (it wins over
            // patentImage) — update the current column + its cached sibling
            // data so the new image shows without a reload.
            if (annotationId) {
              setFeatureColumns((prev) =>
                prev
                  ? prev.map((c) =>
                      c.key === annotationId
                        ? { ...c, patentFigureUrl: displayUrl }
                        : c
                    )
                  : prev
              );
              const cached = siblingCacheRef.current[annotationId];
              if (cached) {
                siblingCacheRef.current[annotationId] = {
                  ...cached,
                  patentImage: {
                    ...(cached.patentImage || {}),
                    patent_figure_path: displayUrl,
                  },
                };
              }
            }
            toast.success("Patent Image Changed");
          }
          setPatentImageSaving(false);
        }}
      />
    )}

    {/* Change-Image in flight — ring spinner at viewport center (loader convention) */}
    {patentImageSaving && (
      <div
        className="fixed inset-0 flex items-center justify-center pointer-events-none"
        style={{ zIndex: 1400 }}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )}

    {isVijilizing && (
      <div className="fixed h-screen inset-0 bg-black bg-opacity-80 flex items-center justify-center backdrop-blur-sm" style={{ zIndex: 9999 }}>
        <div className="bg-white p-8 rounded-lg shadow-lg flex flex-col items-center space-y-4">
          <img
            src={loginLogo}
            alt="Generating..."
            className="w-32 h-auto animate-pulse"
          />
          <div className="text-center">
            <h3 className="text-lg font-semibold text-slate-800 mb-1">
              Vijilizing...
            </h3>
          </div>
        </div>
      </div>
    )}

    <CitationsSidebar
      isShowSideBarCitations={isShowSideBarCitations}
      setIsShowSideBarCitations={setIsShowSideBarCitations}
      citationsData={citationsData}
      selectedCitation={selectedCitation}
      setSelectedCitation={setSelectedCitation}
      partsListData={partsListData}
      activeTab={citationsTab}
      onTabChange={setCitationsTab}
    />
  </>
  );
};

export default MarkdownViewer;
